import { Component, OnInit, AfterViewInit, OnDestroy, inject, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormControl, ReactiveFormsModule, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { CardModule } from 'primeng/card';
import { InputNumber } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { Tooltip } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { Subject, debounceTime, distinctUntilChanged, filter, map, takeUntil } from 'rxjs';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { CrossReferenceService } from '../../../services/mbrs/cross-reference.service';
import { CalculationService } from '../../../services/mbrs/calculation.service';
import { EqualityValidationService, EqualityValidationError } from '../../../services/mbrs/equality-validation.service';
import { CashFlowReconciliationService } from '../../../services/mbrs/cash-flow-reconciliation.service';
import { differsAtCentPrecision } from '../../../services/mbrs/validation-field-key.util';
import { ValidationContextService } from '../../../services/mbrs/validation-context.service';
import { FormSchemaService } from '../../../services/mbrs/form-schema.service';
import { EnvironmentService } from '../../../services/common/environment.service';
import { RuntimeConfigService } from '../../../services/common/runtime-config.service';
import { MappingStatusService } from '../../../services/mbrs/mapping-status.service';
import { ExtractedSumWarningService, EXTRACTED_SUM_MARKER } from '../../../services/mbrs/extracted-sum-warning.service';
import { SummableControl } from '../../../services/mbrs/extracted-sum-warning';
import { ExtractedSumBaselineDebugService } from '../../../services/common/extracted-sum-baseline-debug.service';
import { SubmitForValidationComponent } from "../../common/submit-for-validation/submit-for-validation.component";
import { NegativeHighlightDirective } from '../../../directives/negative-highlight.directive';
import { SchemaNumericDirective } from '../../../directives/schema-numeric.directive';
import { NumericFieldPolicy, resolveNumericPolicy } from '../../../utils/numeric-policy.util';
import { NegativeLabelPipe } from '../../../pipes/negative-label.pipe';
import { SkeletonModule } from 'primeng/skeleton';
import {
  CROSS_SHEET_VALIDATION_DEBOUNCE_MS,
  FORM_CALCULATION_DEBOUNCE_MS,
} from '../../../utils/debounce-times.util';
import { buildEditModeLivePipeline } from '../../../utils/form-edit-pipeline.util';
import { pickSubset } from '../../../utils/pick-subset.util';
import { adoptIntoInlineEditSheet } from '../../../utils/adopt-remote-sheet-state.util';
import { isRowVisible, isCollapsibleHeader as isCollapsibleHeaderRule, headerLevelClass } from '../../../utils/section-visibility.util';
import {
  pickRole18SchemaFile,
  role18AccountingStandardToTaxonomy,
} from '../../../services/mbrs/validators/role18-cash-flows-indirect.rules';
import {
  showConsolidated as showConsolidatedCells,
  hasPreviousYear as hasPreviousYearCells,
} from '../../../services/mbrs/validators/dimension-visibility.util';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { isControlMandatory } from '../../../utils/required-field.util';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';
import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { FieldLinkTargetDirective } from '../../../directives/field-link-target.directive';
import {
  referencedSheetKeysFromSchema,
  recomputeReferencedSheets,
} from '../../../services/mbrs/cross-sheet-recompute.util';
import {
  } from '../../../utils/toast-durations.util';
import { computedControlPredicate, revealValidationErrors, enableDualColumnRowAndRevealVisible } from '../../../utils/validation-reveal.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';

interface FieldControl {
  qname: string;
  label: string;
  type: string;
  required?: boolean;
  ui: {
    controlType?: string;
    control?: string;
    currency?: string;
    placeholder?: string;
    dualColumn?: boolean;
    calculated?: boolean;
    readonly?: boolean;
    calculation?: string;
    level?: number;
    note?: string;
    decimalPlaces?: number;
    linkTo?: {
      role: string;
      qname: string;
    };
  };
}

interface FormSchema {
  role: string;
  sheetId: string;
  label: string;
  description: string;
  controls: FieldControl[];
}

@Component({
  selector: 'app-role18-520000-statement-of-cash-flows-indirect',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputNumber,
    ButtonModule,
    Toast,
    Tooltip,
    SubmitForValidationComponent,
    NegativeHighlightDirective,

    SchemaNumericDirective,
    NegativeLabelPipe,
    SkeletonModule,
    FieldLinkTargetDirective,
  ],
  providers: [MessageService],
  templateUrl: './role18-520000-statement-of-cash-flows-indirect.component.html',
  styleUrls: ['./role18-520000-statement-of-cash-flows-indirect.component.css']
})
export class Role18520000StatementOfCashFlowsIndirectComponent implements OnInit, AfterViewInit, OnDestroy {
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private stateService = inject(FilingFormStateService);
  private crossRefService = inject(CrossReferenceService);
  private calculationService = inject(CalculationService);
  private equalityValidation = inject(EqualityValidationService);
  private cashFlowReconciliation = inject(CashFlowReconciliationService);
  private validationContext = inject(ValidationContextService);
  private formSchemaService = inject(FormSchemaService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private envService = inject(EnvironmentService);
  private cdr = inject(ChangeDetectorRef);
  private runtimeConfig = inject(RuntimeConfigService);
  private mappingStatus = inject(MappingStatusService);
  private extractedSumWarning = inject(ExtractedSumWarningService);
  private baselineDebug = inject(ExtractedSumBaselineDebugService);
  readonly fieldNoteTooltipsEnabled = this.runtimeConfig.fieldNoteTooltipsEnabled;
  private destroy$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);

  statementOfCashFlowsIndirectForm!: FormGroup;
  schema: FormSchema | null = null;
  isLoading = true;
  isValidationComplete = false;
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Statement of Cash Flows (Indirect Method) (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.statementOfCashFlowsIndirectMethod
    );
  }

  // Column visibility derives live on every read from the shared, config-
  // driven resolver via CrossReferenceService.getVisibility() — the SAME
  // resolver + central registry (SHEET_KEYS.statementOfCashFlowsIndirectMethod
  // → NATURE / PREP gates) that feeds the pure
  // role18CashFlowsIndirectValidator, so the mounted editor and the headless
  // listing cannot drift on which columns are shown/validated. A previously-
  // cached mount-time snapshot drifted whenever role18 mounted before
  // role0/role1 finished hydrating, producing a ParityCheck mismatch; the live
  // getter avoids that.
  get dimensionVisibility() {
    return this.crossRefService.getVisibility(
      SHEET_KEYS.statementOfCashFlowsIndirectMethod
    );
  }

  get showConsolidatedColumns(): boolean {
    return showConsolidatedCells(this.dimensionVisibility);
  }

  get hasPreviousYear(): boolean {
    return hasPreviousYearCells(this.dimensionVisibility);
  }

  get isDev(): boolean {
    return this.envService.isDevelopment();
  }

  expandedSections = new Set<string>(); // Track which sections are expanded

  /** Bound as a property so the directive gets a stable, `this`-correct function. */
  protected readonly isSectionHeaderFn = (control: any): boolean =>
    this.isSectionHeader(control);
  private editingRows = new Map<string, { [key: string]: number | null }>(); // Track rows in edit mode with original values
  private calculatedTotalKeys: string[] = [];

  /** Row Mapping-Status frozen at edit-enter; while a row is mid-edit its badge
   *  shows this committed value (not the live in-edit recompute) so the pencil
   *  alone never flips it to 'Edited'. Cleared on Tick/Cancel → badge recomputes. */
  private frozenRowStatus = new Map<string, 'Failed' | 'Edited' | 'Validated*' | 'Validated'>();

  private validationErrors = new Map<string, string[]>();
  private equalityValidationErrors = new Map<string, EqualityValidationError[]>(); // Track equality validation errors
  private mandatoryFieldErrors = new Set<string>(); // Track qnames with mandatory field errors (for red/Failed status)
  private isCancellingChanges = false; // Flag to skip validation during cancel flow
  // Tracks qnames written by the cross-sheet validateAllEqualityRules — preserved
  // through same-sheet recomputes during edit so frozen cross-sheet errors stay visible.
  private crossSheetErrorQnames = new Set<string>();

  /**
   * Get currency symbol with unit from Role-1
   * e.g., "RM'000", "$'000", "€'000"
   */
  get currencyUnit(): string {
    const symbol = this.crossRefService.getCurrencySymbol();
    return `${symbol}'000`;
  }

  /**
   * Get formatted date range for current year
   * e.g., "01/01/2024 - 31/12/2024"
   */
  get periodHeaderCurrent(): string {
    return this.crossRefService.getFormattedFinancialYear('current');
  }

  /**
   * Get formatted date range for previous year
   * e.g., "01/01/2023 - 31/12/2023"
   */
  get periodHeaderPrevious(): string {
    return this.crossRefService.getFormattedFinancialYear('previous');
  }

  ngOnInit(): void {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '18',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    this.baselineDebug.bindGlobalShortcut(this.destroyRef);
    this.loadSchema();
  }

  ngAfterViewInit(): void {
    // Future: Setup data inheritance if needed
    // this.setupDataInheritance();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize all sections as expanded by default
   */
  private initializeExpandedSections(): void {
    // Seed EVERY section header (all levels) as expanded so a row's ancestor
    // chain reads as open on load. Seeding is intentionally SEPARATE from
    // collapsibility (`isCollapsibleHeader`): the level-1 sheet-title banner is
    // seeded here but its toggle is governed by the shared rule below.
    if (this.schema) {
      this.schema.controls.forEach(control => {
        if (this.isSectionHeader(control)) {
          this.expandedSections.add(control.qname);
        }
      });
    }
  }

  /**
   * Whether the section header at `index` is collapsible — i.e. it renders a
   * chevron and toggles its descendant rows on click. Delegates to the shared,
   * structural `isCollapsibleHeader` rule (any header deeper than the banner
   * that actually encloses rows — no level ceiling, so it can't drift as the
   * schema gains deeper headers). Cash Flows opts the level-1 title banner back
   * in (`minCollapsibleLevel: 1`) to preserve its long-standing collapse-whole-
   * sheet behaviour.
   */
  isCollapsibleHeader(index: number): boolean {
    return !!this.schema && isCollapsibleHeaderRule(
      this.schema.controls,
      index,
      (control) => this.isSectionHeader(control),
      { minCollapsibleLevel: 1 },
    );
  }

  /**
   * Toggle section expansion
   */
  toggleSection(qname: string): void {
    if (this.expandedSections.has(qname)) {
      this.expandedSections.delete(qname);
    } else {
      this.expandedSections.add(qname);
    }
  }

  /**
   * Check if section is expanded
   */
  isSectionExpanded(qname: string): boolean {
    return this.expandedSections.has(qname);
  }

  /**
   * Check if a row should be shown based on parent section expansion.
   *
   * Delegates to the shared, pure `isRowVisible` helper, which walks the
   * flattened control list and collects ONLY strictly-shallower ancestor
   * headers (a monotonically decreasing level chain). This fixes the prior
   * hand-rolled walk that recorded the first header at each level regardless of
   * nesting: when a deeper section (level-4 "financing activities") physically
   * preceded a shallower one (level-2 "Details of cash flows"), the deeper
   * header was wrongly treated as an ancestor of the Details rows, so
   * collapsing financing also hid the Details rows. See
   * `section-visibility.util.ts` for the rule and its spec.
   */
  shouldShowRow(control: FieldControl, index: number): boolean {
    if (!this.schema) return true;
    return isRowVisible(
      this.schema.controls,
      index,
      (qname) => this.isSectionExpanded(qname),
      (control) => this.isSectionHeader(control)
    );
  }

  /**
   * Load form schema from JSON
   * Dynamically loads MFRS or MPERS schema based on accounting standard from Role-0
   */
  private async loadSchema(): Promise<void> {
    this.isLoading = true;

    // Wait for retrieve-mbrs-data hydration so the accounting standard is
    // populated before schema selection — a mid-hydration refresh mount would
    // otherwise fall back to the MFRS default and MPERS values would never bind
    // (blank form).
    await this.stateService.waitForMbrsHydration('role18');
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.hydrationSettled);

    // Determine which schema to load based on accounting standard.
    // Schema file selection delegated to pickRole18SchemaFile so the
    // editor and role18CashFlowsIndirectValidator share a single
    // source-of-truth for the MFRS/MPERS picker (see rules file).
    const accountingStandard = this.crossRefService.getCrossReferenceData().accountingStandard;
    const schemaFile = pickRole18SchemaFile(accountingStandard);

    try {
      this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
      const data = await fetchSheetSchema<FormSchema>(
        this.loadTraceHandle,
        `assets/forms/${schemaFile}`,
      );

      this.schema = data;
      this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);

      // Preload schemas for sheets referenced by equality validation rules
      // so that error messages show field labels instead of raw qnames.
      // Derive the taxonomy from the SAME MFRS-default predicate the schema
      // picker uses (not crossRefService.isMFRS(), which treats an empty/unknown
      // standard as MPERS) so the schema and the verdict taxonomy cannot diverge.
      const currentTaxonomy = role18AccountingStandardToTaxonomy(accountingStandard);
      await this.validationContext.preloadReferencedSchemas(this.schema, currentTaxonomy);

      this.initializeExpandedSections(); // Initialize expand/collapse state after schema is loaded
      this.buildForm();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
      this.setupEditPipeline(); // Setup reactive edit pipeline (calc → persist → validate)

      // Reveal the sheet's already-computed errors now that the form exists
      // and has been populated. Deterministic replacement for the filing-form
      // container's setTimeout-driven markAllAsTouched, which silently no-ops
      // when a sheet's async init has not yet assigned its FormGroup — see
      // revealValidationErrors' docblock. Touch-only: cannot move the verdict.
      revealValidationErrors(this.statementOfCashFlowsIndirectForm, {
        isComputed: computedControlPredicate(this.schema?.controls),
      });
      await this.loadSavedState();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored); // Wait for all validations to complete before showing page
      this.setupStatePersistence();
    } catch (error) {
      console.error('Error loading schema:', error);
      // Classified rather than reported as a bare schema failure: this one
      // `.catch` also covers the cross-sheet preload, buildForm, the edit
      // pipeline and loadSavedState, so "Failed to load form schema" named
      // the wrong step for most of what can land here.
      this.loadTraceHandle.fail(classifySheetInitError(error), error);
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    }
  }

  /**
   * Custom validator that treats null/undefined/empty string as invalid but allows 0 as valid
   * Used for required fields where 0 is a valid input (e.g., CashAndCashEquivalents:Beginning)
   */
  private notNullValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      // Reject null, undefined, and empty string
      if (value === null || value === undefined || value === '') {
        return { required: true };
      }

      // Allow 0 and all other numbers
      return null;
    };
  }

  /**
   * Build reactive form from schema
   * CRITICAL: Creates 4 FormControls per field (Consolidated Current/Previous + Separate Current/Previous)
   */
  private buildForm(): void {
    if (!this.schema) return;

    const formControls: any = {};

    this.schema.controls.forEach(control => {
      // Skip headers
      if (this.isSectionHeader(control)) return;

      // Determine required validators
      const validators: ValidatorFn[] = [];

      // Apply notNullValidator for fields marked as required in schema
      // This allows null/empty to be invalid while still allowing 0 as a valid value
      if (control.required === true) {
        validators.push(this.notNullValidator());
      }

      if (control.ui.dualColumn) {
        // Use null for non-calculated fields, 0 for calculated/total fields
        // This ensures unmapped API data fields remain as NULL (not converted to 0)
        const defaultValue = control.ui.calculated ? 0 : null;

        // Always create 4 columns: Consolidated (Current, Previous) and Separate (Current, Previous)
        // All fields start disabled (editable fields can be enabled via edit mode)
        // Only create if not already exists (to handle duplicate qnames)
        if (!formControls[this.getXbrlControlName(control.qname, 'Consolidated', 'Current')]) {
          formControls[this.getXbrlControlName(control.qname, 'Consolidated', 'Current')] = new FormControl({ value: defaultValue, disabled: true }, validators);
        }
        if (!formControls[this.getXbrlControlName(control.qname, 'Consolidated', 'Previous')]) {
          formControls[this.getXbrlControlName(control.qname, 'Consolidated', 'Previous')] = new FormControl({ value: defaultValue, disabled: true }, validators);
        }
        if (!formControls[this.getXbrlControlName(control.qname, 'Separate', 'Current')]) {
          formControls[this.getXbrlControlName(control.qname, 'Separate', 'Current')] = new FormControl({ value: defaultValue, disabled: true }, validators);
        }
        if (!formControls[this.getXbrlControlName(control.qname, 'Separate', 'Previous')]) {
          formControls[this.getXbrlControlName(control.qname, 'Separate', 'Previous')] = new FormControl({ value: defaultValue, disabled: true }, validators);
        }
      }
    });

    this.statementOfCashFlowsIndirectForm = this.fb.group(formControls);
  }

  /**
   * Check if control is a section header (including TextBlock)
   */
  isSectionHeader(control: FieldControl): boolean {
    return control.type === 'SectionHeader' || control.type === 'TextBlock' || control.ui.control === 'header' || control.ui.controlType === 'section-header';
  }

  /**
   * PHASE 2: Get XBRL control name using simplified bracket notation
   * Converts from old concatenated format to new bracket format
   *
   * OLD: qnameConsolidatedCurrent
   * NEW: qname[Consolidated][CurrentYear]
   *
   * @param qname The XBRL concept name
   * @param consolidatedOrSeparate 'Consolidated' or 'Separate'
   * @param period 'Current' or 'Previous'
   * @returns Control name in bracket format
   */
  getXbrlControlName(
    qname: string,
    consolidatedOrSeparate: 'Consolidated' | 'Separate',
    period: 'Current' | 'Previous'
  ): string {
    const periodLabel = period === 'Current' ? 'CurrentYear' : 'PreviousYear';
    return `${qname}[${consolidatedOrSeparate}][${periodLabel}]`;
  }

  /**
   * Get header CSS class based on level
   */
  getHeaderClass(level?: number | string): string {
    return headerLevelClass(level);
  }

  /**
   * Check if field is calculated
   */
  /**
   * Does this row's label get a `*`?
   *
   * Delegates to the shared `isControlMandatory` so this sheet cannot drift from
   * the other 16 — the marker used to live only inside
   * `<label class="field-label">`, which this sheet does not render, so its
   * mandatory rows went unmarked from the initial scaffolding until 2026-08-11.
   * See `isControlMandatory` in `required-field.util.ts`.
   *
   * `crossSheetValue` is supplied because a cross-sheet `requiredIf` degrades to
   * "not mandatory" without it; every other declaration form this sheet uses is
   * unconditional and needs no context. Same-sheet `selfData` is deliberately
   * NOT passed: this sheet keys its values per cell
   * (`qname[Dimension][Period]`), not by bare qname, so a same-sheet lookup
   * would silently miss. Add it — with the right key shape — if a same-sheet
   * conditional rule is ever declared here.
   */
  isFieldRequired(control: any): boolean {
    return isControlMandatory(control, {
      crossSheetValue: (sheet, field) =>
        (this.stateService.getSheetData(sheet as any) as any)?.[field],
    });
  }

  isCalculatedField(control: FieldControl): boolean {
    return control.ui.calculated === true;
  }

  /**
   * Wire the single debounced live pipeline: calc → validateSameSheet → validateCrossSheet → status.
   * No persist callback — commit only happens on saveEditMode (Tick).
   */
  private setupEditPipeline(): void {
    buildEditModeLivePipeline({
      source$: this.statementOfCashFlowsIndirectForm.valueChanges,
      debounceMs: FORM_CALCULATION_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      // no shouldSkip — original had no guard
      calculate: () => this.calculateTotals(),
      validateSameSheet: () => this.validateSameSheet(),
      validateCrossSheet: () => this.validateCrossSheet(),
      evaluateStatus: () => this.evaluateSheetStatus(),
    });
  }

  /**
   * Calculate all totals for indirect method cash flow statement
   * Now uses CalculationService for centralized calculation logic
   */
  private calculateTotals(): void {
    if (!this.schema) return;

    // NEW: Use CalculationService for centralized calculation logic
    // Get current form data
    const formData = this.statementOfCashFlowsIndirectForm.getRawValue();

    // Determine if we're using MFRS or MPERS based on schema role
    const isMFRS = this.schema.role.includes('mfrs');
    const xbrlPrefix = isMFRS ? 'ifrs-full' : 'ifrs-smes';

    // Call calculation service to get calculated totals
    const calculatedTotals = this.calculationService.calculateTotals_520000(
      formData,
      xbrlPrefix as 'ifrs-full' | 'ifrs-smes'
    );

    // Register calculated keys so isSheetModified() skips them
    this.calculatedTotalKeys = Object.keys(calculatedTotals);
    this.stateService.registerCalculatedKeys(
      SHEET_KEYS.statementOfCashFlowsIndirectMethod,
      Object.keys(calculatedTotals)
    );

    // Apply calculated totals to form controls
    Object.keys(calculatedTotals).forEach((controlName) => {
      const control = this.statementOfCashFlowsIndirectForm.get(controlName);
      if (control) {
        control.setValue(calculatedTotals[controlName], { emitEvent: false });
        // Mark as pristine to prevent calculated fields from showing 'Edited' status
        control.markAsPristine();
      }
    });
  }

  /* ============================================================================
   * OLD COMPONENT-BASED CALCULATION LOGIC (COMMENTED OUT)
   * Migrated to CalculationService.calculateTotals_520000()
   * Kept for reference and potential rollback if needed
   * ============================================================================

  // /**
  //  * Calculate totals for MFRS schema
  //  */
  // private calculateMFRSTotals(suffix: string): void {
  //     // 1. Aggregate Profit/Loss Before Tax (= ProfitLossBeforeTax)
  //     const profitLossBeforeTax = this.getFieldValue('ifrs-full:ProfitLossBeforeTax', suffix);
  //     this.setValue('ssmt-mfrs:AggregateProfitLossBeforeTax', suffix, profitLossBeforeTax);
  // 
  //     // 2. Total Impairment Loss (sum of all impairment fields)
  //     const totalImpairment =
  //       this.getFieldValue('ifrs-full:ImpairmentLossRecognisedInProfitOrLossPropertyPlantAndEquipment', suffix) +
  //       this.getFieldValue('ifrs-full:ImpairmentLossRecognisedInProfitOrLossInvestmentProperty', suffix) +
  //       this.getFieldValue('ssmt-mfrs:ImpairmentLossRecognisedInProfitOrLossOfPrepaidLeaseRental', suffix) +
  //       this.getFieldValue('ifrs-full:ImpairmentLossRecognisedInProfitOrLossIntangibleAssetsOtherThanGoodwill', suffix) +
  //       this.getFieldValue('ssmt-mfrs:ImpairmentLossRecognisedInProfitOrLossOfInvestmentInSubsidiaries', suffix) +
  //       this.getFieldValue('ssmt-mfrs:ImpairmentLossRecognisedInProfitOrLossOfInvestmentsInAssociates', suffix) +
  //       this.getFieldValue('ssmt-mfrs:ImpairmentLossRecognisedInProfitOrLossOfInvestmentsInJointVentures', suffix) +
  //       this.getFieldValue('ssmt-mfrs:ImpairmentLossRecognisedInProfitOrLossOnOtherInvestments', suffix) +
  //       this.getFieldValue('ssmt-mfrs:ImpairmentLossRecognisedInProfitOrLossOnOtherFinancialAssetsOrReceivables', suffix);
  //     this.setValue('ifrs-full:ImpairmentLossRecognisedInProfitOrLoss', suffix, totalImpairment);
  // 
  //     // 3. Total Gain/Loss on Disposal of Non-current Assets
  //     const totalDisposalGains =
  //       this.getFieldValue('ifrs-full:GainsLossesOnDisposalsOfPropertyPlantAndEquipment', suffix) +
  //       this.getFieldValue('ifrs-full:GainsLossesOnDisposalsOfInvestmentProperties', suffix);
  //     this.setValue('ifrs-full:GainsLossesOnDisposalsOfNoncurrentAssets', suffix, totalDisposalGains);
  // 
  //     // 4. Total Write-offs
  //     const totalWriteoffs =
  //       this.getFieldValue('ifrs-full:WritedownsReversalsOfPropertyPlantAndEquipment', suffix) +
  //       this.getFieldValue('ssmt-mfrs:WritedownsReversalsOfIntangibleAssets', suffix) +
  //       this.getFieldValue('ssmt-mfrs:WritedownsReversalsOfInvestments', suffix) +
  //       this.getFieldValue('ifrs-full:WritedownsReversalsOfInventories', suffix) +
  //       this.getFieldValue('ssmt-mfrs:WritedownsOfTradeAndOtherReceivables', suffix) +
  //       this.getFieldValue('ssmt-mfrs:WritedownsReversalsOfOtherFinancialAssets', suffix);
  //     this.setValue('ssmt-mfrs:WritedownsReversalsOfAssets', suffix, totalWriteoffs);
  // 
  //     // 5. Total Adjustments to Reconcile Profit/Loss (sum of all adjustment fields)
  //     // Note: Some fields are subtracted (gains, income) instead of added
  //     const totalAdjustments =
  //       this.getFieldValue('ifrs-full:AdjustmentsForDepreciationExpense', suffix) +
  //       this.getFieldValue('ifrs-full:AdjustmentsForAmortisationExpense', suffix) -
  //       this.getFieldValue('ifrs-full:AdjustmentsForDividendIncome', suffix) +
  //       this.getFieldValue('ifrs-full:AdjustmentsForSharebasedPayments', suffix) -
  //       this.getFieldValue('ifrs-full:AdjustmentsForGainsLossesOnFairValueAdjustmentInvestmentProperty', suffix) +
  //       this.getFieldValue('ssmt-mfrs:AdjustmentsForUnrealisedForeignExchangeGainLoss', suffix) -
  //       this.getFieldValue('ifrs-full:AdjustmentsForInterestIncome', suffix) +
  //       this.getFieldValue('ssmt-mfrs:AdjustmentsForPerformanceBasedEmployeeShareSchemeAndOtherExpenses', suffix) +
  //       this.getFieldValue('ssmt-mfrs:AdjustmentsForShareOfProfitsOfJointVentures', suffix) +
  //       this.getFieldValue('ssmt-mfrs:AdjustmentsForShareOptionExpenses', suffix) -
  //       this.getFieldValue('ifrs-full:AdjustmentsForUndistributedProfitsOfAssociates', suffix) +
  //       this.getFieldValue('ifrs-full:AdjustmentsForFinanceCosts', suffix) -
  //       this.getFieldValue('ifrs-full:AdjustmentsForFinanceIncome', suffix) -
  //       this.getFieldValue('ssmt-mfrs:PaymentsForLeasesOfLowValueAssetsAndVariableLeasePaymentsNotIncludedInMeasurementOfLeaseLiability', suffix) +
  //       this.getFieldValue('ssmt-mfrs:AdjustmentsForOtherNoncurrentAssets', suffix) +
  //       totalImpairment -
  //       // this.getFieldValue('ifrs-full:GainsLossesOnDisposalsOfPropertyPlantAndEquipment', suffix) +
  //       // this.getFieldValue('ifrs-full:GainsLossesOnDisposalsOfInvestmentProperties', suffix) -
  //       totalDisposalGains -
  //       this.getFieldValue('ssmt-mfrs:GainsLossesOnDisposalOfSubsidiaries', suffix) -
  //       this.getFieldValue('ssmt-mfrs:GainsLossesOnDisposalOfAssociates', suffix) -
  //       this.getFieldValue('ssmt-mfrs:GainsLossesOnDisposalOfJointVentures', suffix) -
  //       this.getFieldValue('ssmt-mfrs:GainsLossesOnDisposalOfOtherInvestments', suffix) -
  //       this.getFieldValue('ssmt-mfrs:RealisedGainTransferredFromEquityOnForeignCurrencyExchange', suffix) +
  //       this.getFieldValue('ifrs-full:OtherAdjustmentsForNoncashItems', suffix) +
  //       totalWriteoffs +
  //       this.getFieldValue('ssmt-mfrs:WriteBackOfExpenses', suffix) +
  //       this.getFieldValue('ifrs-full:OtherAdjustmentsToReconcileProfitLoss', suffix);
  //     this.setValue('ifrs-full:AdjustmentsForReconcileProfitLoss', suffix, totalAdjustments);
  // 
  //     // 6. Operating Surplus/Deficit Before Working Capital Changes
  //     const operatingSurplus = profitLossBeforeTax + totalAdjustments;
  //     this.setValue('ifrs-full:CashFlowsFromUsedInOperationsBeforeChangesInWorkingCapital', suffix, operatingSurplus);
  // 
  //     // 7. Total Changes in Working Capital
  //     const totalWorkingCapitalChanges =
  //       this.getFieldValue('ifrs-full:AdjustmentsForDecreaseIncreaseInInventories', suffix) -
  //       this.getFieldValue('ifrs-full:AdjustmentsForDecreaseIncreaseInTradeAndOtherReceivables', suffix) -
  //       this.getFieldValue('ifrs-full:AdjustmentsForIncreaseDecreaseInTradeAndOtherPayables', suffix) -
  //       this.getFieldValue('ssmt-mfrs:AdjustmentsForDecreaseIncreaseInPropertyDevelopmentCosts', suffix) -
  //       this.getFieldValue('ifrs-full:AdjustmentsForDecreaseIncreaseInContractAssets', suffix) -
  //       this.getFieldValue('ssmt:AdjustmentsForDecreaseIncreaseInContractLiabilities', suffix);
  //     this.setValue('ifrs-full:IncreaseDecreaseInWorkingCapital', suffix, totalWorkingCapitalChanges);
  // 
  //     // 8. Cash Generated from Operations
  //     const cashGeneratedFromOps = operatingSurplus + totalWorkingCapitalChanges;
  //     this.setValue('ifrs-full:CashFlowsFromUsedInOperations', suffix, cashGeneratedFromOps);
  // 
  //     // 9. Net Cash from Operating Activities
  //     const netCashFromOperating =
  //       cashGeneratedFromOps + // row 88
  //       this.getFieldValue('ssmt-mfrs:PaymentsForLeasesOfLowValueAssetsAndVariableLeasePaymentsNotIncludedInMeasurementOfLeaseLiability', suffix) + // row 45
  //       this.getFieldValue('ifrs-full:DividendsReceivedClassifiedAsOperatingActivities', suffix) +
  //       this.getFieldValue('ifrs-full:DividendsPaidClassifiedAsOperatingActivities', suffix) +
  //       this.getFieldValue('ifrs-full:InterestReceivedClassifiedAsOperatingActivities', suffix) +
  //       this.getFieldValue('ifrs-full:InterestPaidClassifiedAsOperatingActivities', suffix) +
  //       this.getFieldValue('ssmt-mfrs:PaymentsForRetirementBenefitsScheme', suffix) +
  //       this.getFieldValue('ifrs-full:IncomeTaxesPaidRefundClassifiedAsOperatingActivities', suffix) +
  //       this.getFieldValue('ifrs-full:OtherInflowsOutflowsOfCashClassifiedAsOperatingActivities', suffix);
  //     this.setValue('ifrs-full:CashFlowsFromUsedInOperatingActivities', suffix, netCashFromOperating);
  // 
  //     // 10. Net Cash from Investing Activities (sum of all 30 investing fields)
  //     const netCashFromInvesting =
  //       this.getFieldValue('ifrs-full:CashFlowsFromLosingControlOfSubsidiariesOrOtherBusinessesClassifiedAsInvestingActivities', suffix) -
  //       this.getFieldValue('ifrs-full:CashFlowsUsedInObtainingControlOfSubsidiariesOrOtherBusinessesClassifiedAsInvestingActivities', suffix) +
  //       this.getFieldValue('ifrs-full:ProceedsFromSalesOfInterestsInAssociates', suffix) +
  //       this.getFieldValue('ssmt-mfrs:OtherCashPaymentsToAcquireInterestsInAssociates', suffix) +
  //       this.getFieldValue('ifrs-full:OtherCashReceiptsFromSalesOfInterestsInJointVenturesClassifiedAsInvestingActivities', suffix) -
  //       this.getFieldValue('ifrs-full:OtherCashPaymentsToAcquireInterestsInJointVenturesClassifiedAsInvestingActivities', suffix) +
  //       this.getFieldValue('ifrs-full:CashReceiptsFromRepaymentOfAdvancesAndLoansMadeToOtherPartiesClassifiedAsInvestingActivities', suffix) -
  //       this.getFieldValue('ifrs-full:CashAdvancesAndLoansMadeToOtherPartiesClassifiedAsInvestingActivities', suffix) +
  //       this.getFieldValue('ssmt-mfrs:DepositPlacedWithInvestmentBrokers', suffix) -
  //       this.getFieldValue('ifrs-full:OtherCashPaymentsToAcquireEquityOrDebtInstrumentsOfOtherEntitiesClassifiedAsInvestingActivities', suffix) +
  //       this.getFieldValue('ifrs-full:OtherCashReceiptsFromSalesOfEquityOrDebtInstrumentsOfOtherEntitiesClassifiedAsInvestingActivities', suffix) -
  //       this.getFieldValue('ssmt-mfrs:ProceedsFromDisposalOfNetCashAndCashEqivalentsDisposedOffFromDiscontinuingOperatrions', suffix) +
  //       this.getFieldValue('ssmt-mfrs:TaxPaidOnGainOnDisposalFromDiscontinuingOperations', suffix) +
  //       this.getFieldValue('ifrs-full:ProceedsFromGovernmentGrantsClassifiedAsInvestingActivities', suffix) +
  //       this.getFieldValue('ifrs-full:ProceedsFromSalesOfIntangibleAssetsClassifiedAsInvestingActivities', suffix) +
  //       this.getFieldValue('ifrs-full:ProceedsFromOtherLongtermAssetsClassifiedAsInvestingActivities', suffix) +
  //       this.getFieldValue('ifrs-full:ProceedsFromSalesOfPropertyPlantAndEquipmentClassifiedAsInvestingActivities', suffix) +
  //       this.getFieldValue('ssmt-mfrs:ProceedsFromSaleOfPrepaidLeasePayments', suffix) -
  //       this.getFieldValue('ifrs-full:PurchaseOfIntangibleAssetsClassifiedAsInvestingActivities', suffix) -
  //       this.getFieldValue('ifrs-full:PurchaseOfOtherLongtermAssetsClassifiedAsInvestingActivities', suffix) -
  //       this.getFieldValue('ifrs-full:PurchaseOfPropertyPlantAndEquipmentClassifiedAsInvestingActivities', suffix) +
  //       this.getFieldValue('ifrs-full:DividendsReceivedClassifiedAsInvestingActivities', suffix) +
  //       this.getFieldValue('ifrs-full:InterestReceivedClassifiedAsInvestingActivities', suffix) +
  //       this.getFieldValue('ifrs-full:ProceedsFromSalesOfInvestmentsOtherThanInvestmentsAccountedForUsingEquityMethod', suffix) -
  //       this.getFieldValue('ifrs-full:PurchaseOfInvestmentsOtherThanInvestmentsAccountedForUsingEquityMethod', suffix) -
  //       this.getFieldValue('ifrs-full:PaymentsForDevelopmentProjectExpenditure', suffix) -
  //       this.getFieldValue('ssmt-mfrs:NetRepaymentFromJointVentures', suffix) -
  //       this.getFieldValue('ssmt-mfrs:NetRepaymentFromAssociates', suffix) +
  //       this.getFieldValue('ssmt-mfrs:CapitalDistributionsFromAssociates', suffix) +
  //       this.getFieldValue('ifrs-full:OtherInflowsOutflowsOfCashClassifiedAsInvestingActivities', suffix);
  //     this.setValue('ifrs-full:CashFlowsFromUsedInInvestingActivities', suffix, netCashFromInvesting);
  // 
  //     // 11. Net Cash from Financing Activities (sum of all 18 financing fields)
  //     const netCashFromFinancing =
  //       this.getFieldValue('ssmt-mfrs:PaymentsForPrincipalPortionOfLeaseLiability', suffix) +
  //       this.getFieldValue('ssmt-mfrs:PaymentsForInterestPortionOfLeaseLiability', suffix) +
  //       this.getFieldValue('ifrs-full:ProceedsFromIssuingShares', suffix) +
  //       this.getFieldValue('ssmt-mfrs:ProceedsFromIssueOfSharesInSubsidiaryToNoncontrollingInterests', suffix) +
  //       this.getFieldValue('ifrs-full:ProceedsFromIssuingOtherEquityInstruments', suffix) +
  //       this.getFieldValue('ifrs-full:ProceedsFromExerciseOfOptions', suffix) -
  //       this.getFieldValue('ifrs-full:PaymentsToAcquireOrRedeemEntitysShares', suffix) -
  //       this.getFieldValue('ssmt-mfrs:PaymentsMadeToAcquireNoncontrollingInterest', suffix) -
  //       this.getFieldValue('ifrs-full:PaymentsOfOtherEquityInstruments', suffix) +
  //       this.getFieldValue('ssmt-mfrs:CashPaymentsForIssuanceOfPerpetualSukuk', suffix) +
  //       this.getFieldValue('ifrs-full:ProceedsFromIssueOfBondsNotesAndDebentures', suffix) -
  //       this.getFieldValue('ifrs-full:RepaymentsOfBorrowingsClassifiedAsFinancingActivities', suffix) +
  //       this.getFieldValue('ssmt-mfrs:RepurchaseOfTreasuryShares', suffix) +
  //       this.getFieldValue('ssmt-mfrs:WithdrawalOfBankDeposits', suffix) +
  //       this.getFieldValue('ssmt-mfrs:WithdrawalOfSecuritiesPledgedForBorrowings', suffix) -
  //       this.getFieldValue('ifrs-full:DividendsPaidClassifiedAsFinancingActivities', suffix) -
  //       this.getFieldValue('ifrs-full:InterestPaidClassifiedAsFinancingActivities', suffix) +
  //       this.getFieldValue('ifrs-full:OtherInflowsOutflowsOfCashClassifiedAsFinancingActivities', suffix);
  //     this.setValue('ifrs-full:CashFlowsFromUsedInFinancingActivities', suffix, netCashFromFinancing);
  // 
  //     // 12. Net Increase/Decrease Before Exchange Rate Effects
  //     const netIncreaseBeforeExchange = netCashFromOperating + netCashFromInvesting + netCashFromFinancing;
  //     this.setValue('ifrs-full:IncreaseDecreaseInCashAndCashEquivalentsBeforeEffectOfExchangeRateChanges', suffix, netIncreaseBeforeExchange);
  // 
  //     // 13. Net Increase/Decrease After Exchange Rate Effects
  //     const exchangeRateEffect = this.getFieldValue('ifrs-full:EffectOfExchangeRateChangesOnCashAndCashEquivalents', suffix);
  //     const netIncreaseAfterExchange = netIncreaseBeforeExchange + exchangeRateEffect;
  //     this.setValue('ifrs-full:IncreaseDecreaseInCashAndCashEquivalents', suffix, netIncreaseAfterExchange);
  // 
  //     // 14. Cash and Cash Equivalents Details (reconciliation)
  //     const cashAndBank = this.getFieldValue('ssmt:CashAndBankBalances', suffix);
  //     const bankOverdraft = this.getFieldValue('ifrs-full:BankOverdraftsClassifiedAsCashEquivalents', suffix);
  //     const otherAdjustments = this.getFieldValue('ssmt:OtherAdjustmentsToReconcileCashAndCashEquivalents', suffix);
  //     const cashAndCashEquivalentsAtEndOfPeriodDetails = cashAndBank - bankOverdraft + otherAdjustments;
  //     this.setValue('ifrs-full:CashAndCashEquivalents', suffix, cashAndCashEquivalentsAtEndOfPeriodDetails);
  //   }

  //   /**
  //    * Calculate totals for MPERS schema
  //    */
  //   private calculateMPERSTotals(suffix: string): void {
  //     // 1. Aggregate Profit/Loss Before Tax (continuing + discontinued)
  //     const profitContinuing = this.getFieldValue('ifrs-smes:ProfitLossBeforeTax', suffix);
  //     const profitDiscontinued = this.getFieldValue('ifrs-smes:ProfitLossFromDiscontinuedOperations', suffix);
  //     const aggregateProfitLoss = profitContinuing + profitDiscontinued;
  //     this.setValue('ssmt-mpers:AggregateProfitLossBeforeTax', suffix, aggregateProfitLoss);
  // 
  //     // 2. Total Adjustments to Reconcile Profit/Loss (sum of 28 adjustment fields)
  //     // Note: Fields with Weight -1 in taxonomy are subtracted
  //     const totalAdjustments =
  //       this.getFieldValue('ifrs-smes:AdjustmentsForNoncashIncomeTaxExpense', suffix) +
  //       this.getFieldValue('ifrs-smes:AdjustmentsForNoncashFinanceCosts', suffix) +
  //       this.getFieldValue('ifrs-smes:AdjustmentsForIncomeTaxExpense', suffix) +
  //       this.getFieldValue('ifrs-smes:AdjustmentsForFinanceCosts', suffix) -
  //       this.getFieldValue('ssmt-mpers:AdjustmentsForFinanceIncome', suffix) +
  //       this.getFieldValue('ifrs-smes:AdjustmentsForDecreaseIncreaseInInventories', suffix) +
  //       this.getFieldValue('ifrs-smes:AdjustmentsForDecreaseIncreaseInTradeAccountReceivable', suffix) +
  //       this.getFieldValue('ifrs-smes:AdjustmentsForDecreaseIncreaseInOtherOperatingReceivables', suffix) +
  //       this.getFieldValue('ifrs-smes:AdjustmentsForIncreaseDecreaseInTradeAccountPayable', suffix) +
  //       this.getFieldValue('ifrs-smes:AdjustmentsForIncreaseDecreaseInOtherOperatingPayables', suffix) -
  //       this.getFieldValue('ssmt-mpers:AdjustmentsForDividendIncome', suffix) +
  //       this.getFieldValue('ssmt-mpers:AdjustmentsForDepreciationExpense', suffix) +
  //       this.getFieldValue('ssmt-mpers:AdjustmentsForAmortisationExpense', suffix) +
  //       this.getFieldValue('ifrs-smes:ImpairmentLossRecognisedInProfitOrLossIntangibleAssetsOtherThanGoodwill', suffix) +
  //       this.getFieldValue('ifrs-smes:ImpairmentLossRecognisedInProfitOrLossPropertyPlantAndEquipment', suffix) +
  //       this.getFieldValue('ifrs-smes:ReversalOfInventoryWritedown', suffix) +
  //       this.getFieldValue('ifrs-smes:AdjustmentsForProvisions', suffix) +
  //       this.getFieldValue('ifrs-smes:AdjustmentsForUnrealisedForeignExchangeLossesGains', suffix) +
  //       this.getFieldValue('ifrs-smes:AdjustmentsForSharebasedPayments', suffix) +
  //       this.getFieldValue('ifrs-smes:AdjustmentsForFairValueGainsLosses', suffix) -
  //       this.getFieldValue('ssmt-mpers:AdjustmentsForShareOfProfitOfEquityAccountedAssociatesAndJointVenturesNetOfTax', suffix) -
  //       this.getFieldValue('ifrs-smes:AdjustmentsForAccruedIncomeExpensesNotYetReceivedPaid', suffix) +
  //       this.getFieldValue('ifrs-smes:OtherAdjustmentsForNoncashItems', suffix) -
  //       this.getFieldValue('ssmt-mpers:GainsLossesOnDisposalsOfPropertyPlantAndEquipment', suffix) -
  //       this.getFieldValue('ssmt-mpers:GainsLossesOnDisposalsOfInvestmentProperties', suffix) -
  //       this.getFieldValue('ssmt-mpers:GainsLossesOnDisposalOfOtherInvestments', suffix) +
  //       this.getFieldValue('ifrs-smes:OtherAdjustmentsForWhichCashEffectsAreInvestingOrFinancingCashFlow', suffix) +
  //       this.getFieldValue('ifrs-smes:OtherAdjustmentsToReconcileProfitLoss', suffix);
  //     this.setValue('ifrs-smes:AdjustmentsForReconcileProfitLoss', suffix, totalAdjustments);
  // 
  //     // 3. Cash Generated from Operations
  //     const cashGenerated = aggregateProfitLoss + totalAdjustments;
  //     this.setValue('ssmt-mpers:CashFlowsFromUsedInOperations', suffix, cashGenerated);
  // 
  //     // 4. Net Cash from Operating Activities
  //     const netCashFromOperating =
  //       cashGenerated +
  //       this.getFieldValue('ifrs-smes:DividendsPaidClassifiedAsOperatingActivities', suffix) +
  //       this.getFieldValue('ifrs-smes:DividendsReceivedClassifiedAsOperatingActivities', suffix) +
  //       this.getFieldValue('ifrs-smes:InterestPaidClassifiedAsOperatingActivities', suffix) +
  //       this.getFieldValue('ifrs-smes:InterestReceivedClassifiedAsOperatingActivities', suffix) +
  //       this.getFieldValue('ifrs-smes:IncomeTaxesPaidRefundClassifiedAsOperatingActivities', suffix) +
  //       this.getFieldValue('ifrs-smes:OtherInflowsOutflowsOfCashClassifiedAsOperatingActivities', suffix);
  //     this.setValue('ifrs-smes:CashFlowsFromUsedInOperatingActivities', suffix, netCashFromOperating);
  // 
  //     // 5. Net Cash from Investing Activities (sum of 19 investing fields)
  //     // Note: Fields with Weight -1 in taxonomy are subtracted
  //     const netCashFromInvesting =
  //       this.getFieldValue('ifrs-smes:CashFlowsFromLosingControlOfSubsidiariesOrOtherBusinessesClassifiedAsInvestingActivities', suffix) -
  //       this.getFieldValue('ifrs-smes:CashFlowsUsedInObtainingControlOfSubsidiariesOrOtherBusinessesClassifiedAsInvestingActivities', suffix) +
  //       this.getFieldValue('ifrs-smes:OtherCashReceiptsFromSalesOfInterestsInJointVenturesClassifiedAsInvestingActivities', suffix) -
  //       this.getFieldValue('ifrs-smes:OtherCashPaymentsToAcquireInterestsInJointVenturesClassifiedAsInvestingActivities', suffix) +
  //       this.getFieldValue('ifrs-smes:OtherCashReceiptsFromSalesOfEquityOrDebtInstrumentsOfOtherEntitiesClassifiedAsInvestingActivities', suffix) -
  //       this.getFieldValue('ifrs-smes:OtherCashPaymentsToAcquireEquityOrDebtInstrumentsOfOtherEntitiesClassifiedAsInvestingActivities', suffix) +
  //       this.getFieldValue('ifrs-smes:ProceedsFromSalesOfPropertyPlantAndEquipmentClassifiedAsInvestingActivities', suffix) -
  //       this.getFieldValue('ifrs-smes:PurchaseOfPropertyPlantAndEquipmentClassifiedAsInvestingActivities', suffix) +
  //       this.getFieldValue('ifrs-smes:ProceedsFromSalesOfIntangibleAssetsClassifiedAsInvestingActivities', suffix) -
  //       this.getFieldValue('ifrs-smes:PurchaseOfIntangibleAssetsClassifiedAsInvestingActivities', suffix) +
  //       this.getFieldValue('ifrs-smes:ProceedsFromOtherLongtermAssetsClassifiedAsInvestingActivities', suffix) -
  //       this.getFieldValue('ifrs-smes:PurchaseOfOtherLongtermAssetsClassifiedAsInvestingActivities', suffix) -
  //       this.getFieldValue('ifrs-smes:CashAdvancesAndLoansMadeToOtherPartiesClassifiedAsInvestingActivities', suffix) +
  //       this.getFieldValue('ifrs-smes:CashReceiptsFromRepaymentOfAdvancesAndLoansMadeToOtherPartiesClassifiedAsInvestingActivities', suffix) -
  //       this.getFieldValue('ifrs-smes:CashPaymentsForFutureContractsForwardContractsOptionContractsAndSwapContractsClassifiedAsInvestingActivities', suffix) +
  //       this.getFieldValue('ifrs-smes:CashReceiptsFromFutureContractsForwardContractsOptionContractsAndSwapContractsClassifiedAsInvestingActivities', suffix) +
  //       this.getFieldValue('ifrs-smes:DividendsReceivedClassifiedAsInvestingActivities', suffix) +
  //       this.getFieldValue('ifrs-smes:InterestReceivedClassifiedAsInvestingActivities', suffix) +
  //       this.getFieldValue('ifrs-smes:OtherInflowsOutflowsOfCashClassifiedAsInvestingActivities', suffix);
  //     this.setValue('ifrs-smes:CashFlowsFromUsedInInvestingActivities', suffix, netCashFromInvesting);
  // 
  //     // 6. Net Cash from Financing Activities (sum of 11 financing fields)
  //     // Note: Fields with Weight -1 in taxonomy are subtracted
  //     const netCashFromFinancing =
  //       this.getFieldValue('ifrs-smes:ProceedsFromIssuingShares', suffix) +
  //       this.getFieldValue('ifrs-smes:ProceedsFromIssuingOtherEquityInstruments', suffix) -
  //       this.getFieldValue('ifrs-smes:PaymentsToAcquireOrRedeemEntitysShares', suffix) -
  //       this.getFieldValue('ifrs-smes:PaymentsOfOtherEquityInstruments', suffix) +
  //       this.getFieldValue('ifrs-smes:ProceedsFromBorrowingsClassifiedAsFinancingActivities', suffix) -
  //       this.getFieldValue('ifrs-smes:RepaymentsOfBorrowingsClassifiedAsFinancingActivities', suffix) -
  //       this.getFieldValue('ssmt-mpers:RepaymentOfLoanFromAssociate', suffix) -
  //       this.getFieldValue('ifrs-smes:PaymentsOfFinanceLeaseLiabilitiesClassifiedAsFinancingActivities', suffix) -
  //       this.getFieldValue('ifrs-smes:DividendsPaidClassifiedAsFinancingActivities', suffix) -
  //       this.getFieldValue('ifrs-smes:InterestPaidClassifiedAsFinancingActivities', suffix) +
  //       this.getFieldValue('ifrs-smes:OtherInflowsOutflowsOfCashClassifiedAsFinancingActivities', suffix);
  //     this.setValue('ifrs-smes:CashFlowsFromUsedInFinancingActivities', suffix, netCashFromFinancing);
  // 
  //     // 7. Net Increase/Decrease Before Exchange Rate Effects
  //     const netIncreaseBeforeExchange = netCashFromOperating + netCashFromInvesting + netCashFromFinancing;
  //     this.setValue('ifrs-smes:IncreaseDecreaseInCashAndCashEquivalentsBeforeEffectOfExchangeRateChanges', suffix, netIncreaseBeforeExchange);
  // 
  //     // 8. Net Increase/Decrease After Exchange Rate Effects
  //     const exchangeRateEffect = this.getFieldValue('ifrs-smes:EffectOfExchangeRateChangesOnCashAndCashEquivalents', suffix);
  //     const netIncreaseAfterExchange = netIncreaseBeforeExchange + exchangeRateEffect;
  //     this.setValue('ifrs-smes:IncreaseDecreaseInCashAndCashEquivalents', suffix, netIncreaseAfterExchange);
  // 
  //     // 9. Cash and Cash Equivalents Details (reconciliation)
  //     const cashAndBank = this.getFieldValue('ssmt:CashAndBankBalances', suffix);
  //     const bankOverdraft = this.getFieldValue('ifrs-smes:BankOverdraftsClassifiedAsCashEquivalents', suffix);
  //     const otherAdjustments = this.getFieldValue('ssmt:OtherAdjustmentsToReconcileCashAndCashEquivalents', suffix);
  //     const cashEquivalentsDetails = cashAndBank - bankOverdraft + otherAdjustments;
  //     this.setValue('ifrs-smes:CashAndCashEquivalents', suffix, cashEquivalentsDetails);
  //   }

  //   /**
  //    * Helper method to set a calculated field value
  //    */
  //   private setValue(qname: string, suffix: string, value: number): void {
  //     const controlName = qname + suffix;
  //     const control = this.statementOfCashFlowsIndirectForm.get(controlName);
  //     if (control && control.value !== value) {
  //       control.setValue(value, { emitEvent: false });
  //     }
  //   }
  // 
  //   /**
  //    * Get field value by qname and suffix
  //    */
  //   private getFieldValue(qname: string, suffix: string): number {
  //     const value = this.statementOfCashFlowsIndirectForm.get(qname + suffix)?.value;
  // 
  //     // Handle null, undefined, or empty string
  //     if (value === null || value === undefined || value === '') {
  //       return 0;
  //     }
  // 
  //     // Convert to number if it's a string
  //     const numValue = typeof value === 'number' ? value : parseFloat(value);
  // 
  //     // Return 0 if conversion resulted in NaN
  //     return isNaN(numValue) ? 0 : numValue;
  //   }
  // ============================================================================ */

  /**
   * Validate calculated totals against baseline data
   * Only validates calculated/readonly fields (Totals)
   */
  private validateAgainstApiData(): void {
    // Skip validation during cancel flow - form is reverting to original values
    if (this.isCancellingChanges) {
      return;
    }

    if (!this.schema) return;

    // Delegate the extracted-sum mismatch warning to the shared service. It
    // owns the N8N snapshot read (getSnapshotValues), the summable-total
    // predicate, the per-column comparison and the message text — keeping
    // role18 in lock-step with every other in-scope sheet.
    const columns = [
      { key: '[Consolidated][CurrentYear]', label: 'Consolidated Current' },
      { key: '[Consolidated][PreviousYear]', label: 'Consolidated Previous' },
      { key: '[Separate][CurrentYear]', label: 'Separate Current' },
      { key: '[Separate][PreviousYear]', label: 'Separate Previous' },
    ];
    const warnings = this.extractedSumWarning.computeMismatchWarnings({
      sheetKey: SHEET_KEYS.statementOfCashFlowsIndirectMethod,
      controls: this.schema?.controls ?? [],
      columns,
      getControlName: (qname, key) => qname + key,
      getCurrentValue: (name) => this.statementOfCashFlowsIndirectForm.get(name)?.value,
    });
    this.extractedSumWarning.mergeIntoValidationErrors(this.validationErrors, this.schema?.controls ?? [], warnings);

    // Additional validation: Check if CashAndCashEquivalents:End matches CashAndCashEquivalents:End_occurrence2
    this.validateCashAndCashEquivalentsEnd();
  }

  /**
   * SAME-SHEET validation — safe to run live during edit.
   * Clears + rebuilds validationErrors (except frozen cross-sheet keys) and mandatoryFieldErrors.
   * Runs: validateAgainstApiData (extracted-sum + cash-end reconciliation),
   *       validateConsecutiveYearCash (same-sheet period check),
   *       validateRequiredFields.
   *
   * @param skipCd – when true, skips the terminal cdr.detectChanges() call so
   *   the caller (runAllValidations) can issue a single detectChanges at the end
   *   instead of one per partition.  Defaults to false (live-pipeline path).
   */
  private validateSameSheet(skipCd = false): void {
    // Preserve frozen cross-sheet errors; clear only same-sheet keys.
    for (const key of Array.from(this.validationErrors.keys())) {
      if (!this.crossSheetErrorQnames.has(key)) {
        this.validationErrors.delete(key);
      }
    }
    this.mandatoryFieldErrors.clear();

    this.validateAgainstApiData();
    this.validateConsecutiveYearCash();
    this.validateRequiredFields();

    this.validationErrors = new Map(this.validationErrors);
    if (!skipCd) this.cdr.detectChanges();
  }

  /**
   * Consecutive-year cash check (same-sheet): Beginning of current period must
   * equal End of previous period. Same-sheet because it compares different period
   * columns within this sheet — no other sheet's data is needed as an operand.
   * Moved out of runEqualityValidations (which also ran the CROSS-SHEET
   * validateAllEqualityRules) so it can fire live during edit without the
   * cross-sheet recompute cost.
   *
   * validateConsecutiveYearCash reads ONLY:
   *   1. role18's own sheet values (CashAndCashEquivalents fields)
   *   2. DEI date fields (filingPrerequisites / filingInfo) for year-consecutiveness
   * No role8/role12 data is consumed, so no exportAllData() or recomputeReferencedSheets().
   */
  private validateConsecutiveYearCash(): void {
    if (!this.schema) return;

    const currentTaxonomy = role18AccountingStandardToTaxonomy(
      this.crossRefService.getCrossReferenceData().accountingStandard
    );

    // Build a minimal bag: role18 own values + DEI sheets only.
    // No exportAllData() or recomputeReferencedSheets() needed on the live same-sheet path.
    const formValuesWithAllSheets = {
      [SHEET_KEYS.statementOfCashFlowsIndirectMethod]: this.statementOfCashFlowsIndirectForm.getRawValue(),
      [SHEET_KEYS.filingPrerequisites]: this.stateService.getSheetData(SHEET_KEYS.filingPrerequisites as any),
      [SHEET_KEYS.filingInfo]: this.stateService.getSheetData(SHEET_KEYS.filingInfo as any),
    };

    const consecutiveYearCashErrors = this.cashFlowReconciliation.validateConsecutiveYearCash(
      this.schema,
      formValuesWithAllSheets,
      currentTaxonomy,
      SHEET_KEYS.statementOfCashFlowsIndirectMethod
    );

    Object.keys(consecutiveYearCashErrors).forEach(qname => {
      const errorMessages = consecutiveYearCashErrors[qname].map((err: EqualityValidationError) => err.message);
      const existingErrors = this.validationErrors.get(qname) || [];
      // Preserve BOTH the extracted-sum suggested-change warnings AND the
      // End ↔ End_occurrence2 same-sheet reconciliation errors that
      // validateCashAndCashEquivalentsEnd wrote earlier in THIS same
      // validateSameSheet cycle (identified by their distinctive
      // "(under section …)" reference — see buildMismatchMessage). The
      // consecutive-year roll-forward check and the End-reconciliation check are
      // independent violations that can both fire on the SAME :End /
      // :End_occurrence2 row, so the consecutive-year merge must ADD to — not
      // REPLACE — the recon error. The old `[...apiMismatchWarnings, ...]`
      // dropped the live recon error on :End whenever consecutive-year also
      // failed, so the reconciliation message appeared on :End_occurrence2 but
      // not on :End during edit, only re-surfacing on :End after Tick (when
      // validateCrossSheet re-derives it from the schema equality rule). No
      // stacking risk: validateCashAndCashEquivalentsEnd runs first each cycle
      // and re-sets the recon errors fresh (its own filter drops any prior
      // consecutive-year entry), so this preserve never accumulates.
      const preserved = existingErrors.filter(
        msg => msg.includes(EXTRACTED_SUM_MARKER) || msg.includes('(under section ')
      );
      this.validationErrors.set(qname, [...preserved, ...errorMessages]);
    });
  }

  /**
   * CROSS-SHEET validation — runs only on Tick and from cross-sheet listeners.
   * Clears + rebuilds equalityValidationErrors (from validateAllEqualityRules)
   * and refreshes validationErrors cross-sheet keys tracked in crossSheetErrorQnames.
   * Ends by re-deriving the same-sheet consecutive-year partition and then the
   * MANDATORY partition — in that order. Any writer added to this method must go
   * BEFORE the validateRequiredFields() call at the bottom; see the note there.
   *
   * @param skipCd – when true, skips the terminal cdr.detectChanges() call so
   *   the caller (runAllValidations) can issue a single detectChanges at the end.
   *   Defaults to false (Tick / cross-sheet-listener path).
   */
  private validateCrossSheet(skipCd = false): void {
    if (!this.schema) return;

    // Remove previous cross-sheet entries from validationErrors
    for (const qname of this.crossSheetErrorQnames) {
      this.validationErrors.delete(qname);
    }
    this.crossSheetErrorQnames.clear();
    this.equalityValidationErrors.clear();

    const currentTaxonomy = role18AccountingStandardToTaxonomy(
      this.crossRefService.getCrossReferenceData().accountingStandard
    );

    const allSheetData = this.stateService.exportAllData();
    const referenced = referencedSheetKeysFromSchema(this.schema, SHEET_KEYS.statementOfCashFlowsIndirectMethod);
    const formValuesWithAllSheets = recomputeReferencedSheets(
      { ...allSheetData, [SHEET_KEYS.statementOfCashFlowsIndirectMethod]: this.statementOfCashFlowsIndirectForm.getRawValue() },
      referenced,
      currentTaxonomy,
      (key) => this.validationContext.getCachedSchema(this.validationContext.resolveSheetKey(key) ?? key)
    );

    const errors = this.equalityValidation.validateAllEqualityRules(
      this.schema,
      formValuesWithAllSheets,
      currentTaxonomy,
      SHEET_KEYS.statementOfCashFlowsIndirectMethod
    );

    Object.keys(errors).forEach(qname => {
      this.equalityValidationErrors.set(qname, errors[qname]);

      const errorMessages = errors[qname].map((err: EqualityValidationError) => err.message);
      const existingErrors = this.validationErrors.get(qname) || [];
      // Keep only API mismatch warnings and same-sheet errors; replace cross-sheet entries
      const nonCrossErrors = existingErrors.filter(msg => msg.includes(EXTRACTED_SUM_MARKER));
      this.validationErrors.set(qname, [...nonCrossErrors, ...errorMessages]);
      this.crossSheetErrorQnames.add(qname);
    });

    // Re-derive the SAME-SHEET consecutive-year (roll-forward) partition AFTER
    // the cross-sheet equality merge above.
    //
    // :End / :End_occurrence2 both DECLARE a same-sheet equality, so they land in
    // crossSheetErrorQnames and get fully rewritten here — the clear loop at the
    // top deletes their validationErrors entry and the merge keeps only the
    // EXTRACTED_SUM warnings. That silently dropped the consecutive-year errors
    // (Beginning ↔ End(previous)) that validateSameSheet wrote onto those same
    // rows, so they vanished the moment the user saved (Tick) even though the
    // roll-forward was still violated. Recomputing the consecutive-year partition
    // here restores them on EVERY validateCrossSheet path (Tick + cross-sheet
    // listener) with no staleness, since it reads current DEI dates + own values.
    // It preserves the equality recon just written (its filter keeps
    // "(under section …)") and replaces — never stacks — its own prior entries.
    this.validateConsecutiveYearCash();

    // Re-derive the MANDATORY partition LAST — the invariant that keeps
    // validateRequiredFields' append-based writes safe.
    //
    // validateRequiredFields APPENDS its per-column
    // "<column>: The field is mandatory." lines onto validationErrors[qname],
    // so it is only correct while it is the LAST writer of that map. It used to
    // be, structurally: the pre-split runner ran api-data → equality →
    // required-fields in one pass. Splitting the runner into validateSameSheet +
    // validateCrossSheet left it last on the same-sheet half only, while BOTH
    // writers above (the equality merge and the consecutive-year merge) rebuild
    // validationErrors[qname] from an allow-list of message families that does
    // not include the mandatory one. Any row that gets a cross-sheet or
    // roll-forward error therefore LOST its mandatory lines the moment the user
    // ticked — visibly on CashAndCashEquivalents:Beginning / :End, where filling
    // any ONE dimension column fires the roll-forward check and silently wiped
    // the still-empty columns' mandatory messages (the row stayed red, since
    // mandatoryFieldErrors is untouched here, with no text explaining why).
    //
    // Idempotent by construction: it de-duplicates per column
    // (`if (!existingErrors.includes(mandatoryError))`) and removes the message
    // when the column holds a value, so re-running it cannot stack messages.
    // Preferred over widening the two preserve filters a third time, which would
    // leave the same trap for the next writer added to this method.
    this.validateRequiredFields();

    this.validationErrors = new Map(this.validationErrors);
    if (!skipCd) this.cdr.detectChanges();
  }

  /**
   * Validate that CashAndCashEquivalents:End matches CashAndCashEquivalents:End_occurrence2
   * This ensures the two different calculations of cash at end reconcile:
   * - :End = Beginning + Net Increase/Decrease
   * - :End_occurrence2 = Cash and Bank - Overdrafts + Other Adjustments
   */
  private validateCashAndCashEquivalentsEnd(): void {
    if (!this.schema) return;

    const isMFRS = this.schema.role.includes('mfrs');
    const taxonomy: 'MFRS' | 'MPERS' = isMFRS ? 'MFRS' : 'MPERS';
    // Phase 3: bracket format - only check visible columns
    const columnsToCheck = [
      { suffix: '[Consolidated][CurrentYear]', label: 'Consolidated Current', show: this.showConsolidatedColumns },
      { suffix: '[Consolidated][PreviousYear]', label: 'Consolidated Previous', show: this.showConsolidatedColumns && this.hasPreviousYear },
      { suffix: '[Separate][CurrentYear]', label: 'Separate Current', show: true },
      { suffix: '[Separate][PreviousYear]', label: 'Separate Previous', show: this.hasPreviousYear },
    ].filter(col => col.show);

    // Determine the qnames based on schema type
    const endQname = isMFRS ? 'ifrs-full:CashAndCashEquivalents:End' : 'ifrs-smes:CashAndCashEquivalents:End';
    const endOccurrence2Qname = isMFRS ? 'ifrs-full:CashAndCashEquivalents:End_occurrence2' : 'ifrs-smes:CashAndCashEquivalents:End_occurrence2';

    // Store errors separately for each qname
    const endErrors: string[] = [];
    const endOccurrence2Errors: string[] = [];

    // Display names for error messages (matching formatErrorMessageForField format)
    const endFieldDisplayName = 'Cash and cash equivalents at end of period';
    const endOccurrence2FieldDisplayName = 'Cash and cash equivalents at end of period';
    const sheetDisplayName = 'Statement of Cash Flows (Indirect)';

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`🔍 Validating reconciliation between ${endQname} and ${endOccurrence2Qname}`);

    columnsToCheck.forEach(({ suffix, label: columnLabel }) => {
      const endControlName = endQname + suffix;
      const endOccurrence2ControlName = endOccurrence2Qname + suffix;

      const endControl = this.statementOfCashFlowsIndirectForm.get(endControlName);
      const endOccurrence2Control = this.statementOfCashFlowsIndirectForm.get(endOccurrence2ControlName);

      if (!endControl || !endOccurrence2Control) return;

      // Get values
      const endValue = endControl.value;
      const endOccurrence2Value = endOccurrence2Control.value;

      // Convert to numbers
      const endNum = endValue ? parseFloat(endValue) : 0;
      const endOccurrence2Num = endOccurrence2Value ? parseFloat(endOccurrence2Value) : 0;

      // Check if either field has a non-zero value (validate if at least one has data)
      const hasEndValue = endValue !== null && endValue !== undefined && endValue !== '' && endNum !== 0;
      const hasEndOccurrence2Value = endOccurrence2Value !== null && endOccurrence2Value !== undefined && endOccurrence2Value !== '' && endOccurrence2Num !== 0;

      if (hasEndValue || hasEndOccurrence2Value) {

        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`  ${columnLabel}:`, {
        //   End: endNum,
        //   End_occurrence2: endOccurrence2Num,
        //   difference: difference,
        //   withinTolerance: difference <= tolerance
        // });

        // Round to cents, flag any difference (≥ 0.01): the two End calculations
        // must reconcile. (Old raw `> 0.001` skipped rounding and used a sub-cent
        // band inconsistent with the rest of the equality policy.)
        if (differsAtCentPrecision(endNum, endOccurrence2Num)) {
          // Create error message for endQname (pointing to endOccurrence2)
          const endErrorMessage = this.equalityValidation.formatFieldMismatch({
            columnLabel,
            fieldQname: endOccurrence2FieldDisplayName,
            fieldSheetKey: SHEET_KEYS.statementOfCashFlowsIndirectMethod,
            taxonomy,
            section: 'Details of cash flows',
            sheetLabelOverride: 'Statement of Cash Flows (Indirect Method)',
          });
          endErrors.push(endErrorMessage);

          // Create error message for endOccurrence2Qname (pointing to end)
          const endOccurrence2ErrorMessage = this.equalityValidation.formatFieldMismatch({
            columnLabel,
            fieldQname: endFieldDisplayName,
            fieldSheetKey: SHEET_KEYS.statementOfCashFlowsIndirectMethod,
            taxonomy,
            section: 'Cash flows from (used in) financing activities',
            sheetLabelOverride: 'Statement of Cash Flows (Indirect Method)',
          });
          endOccurrence2Errors.push(endOccurrence2ErrorMessage);

          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`  ❌ Reconciliation mismatch detected!`);
        } else {
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log(`  ✅ Values reconcile within tolerance`);
        }
      }
    });

    // REPLACE (not append) this method's reconciliation messages each cycle.
    //
    // `:End` and `:End_occurrence2` declare a same-sheet schema equality
    // (End ↔ End_occurrence2), so validateAllEqualityRules freezes them into
    // crossSheetErrorQnames. validateSameSheet then PRESERVES those keys across
    // every live-edit cycle — which let the old append (`[...existing, ...new]`)
    // re-add the same reconciliation message on each keystroke, stacking it
    // without bound. Mirror validateConsecutiveYearCash: keep only the
    // extracted-sum warnings already on the key, then set the fresh
    // reconciliation errors (or clear the key when the two End calculations
    // reconcile). The cross-sheet red border is driven by equalityValidationErrors
    // (untouched here), so replacing the message list never drops the verdict.
    const setReconciliationErrors = (qname: string, fresh: string[]): void => {
      const preserved = (this.validationErrors.get(qname) || []).filter(msg => msg.includes(EXTRACTED_SUM_MARKER));
      const next = [...preserved, ...fresh];
      if (next.length > 0) {
        this.validationErrors.set(qname, next);
      } else {
        this.validationErrors.delete(qname);
      }
    };
    setReconciliationErrors(endQname, endErrors);
    setReconciliationErrors(endOccurrence2Qname, endOccurrence2Errors);
  }

  /**
   * Validate required fields in real-time
   * Checks if required fields have null/empty values and adds/removes error messages accordingly
   * Error messages are column-specific (e.g., "Consolidated Current: The field is mandatory.")
   *
   * **Must run LAST in whatever cycle calls it.** It APPENDS onto
   * validationErrors[qname]; every other writer in this component REPLACES that
   * entry, keeping only an allow-list of message families that does not include
   * this one. Both validateSameSheet and validateCrossSheet therefore end with
   * this call.
   */
  private validateRequiredFields(): void {
    if (!this.schema) return;

    // Define columns with their labels and visibility
    const columns: Array<{ type: 'Consolidated' | 'Separate'; period: 'Current' | 'Previous'; label: string; show: boolean }> = [
      { type: 'Consolidated', period: 'Current', label: 'Consolidated Current', show: this.showConsolidatedColumns },
      { type: 'Consolidated', period: 'Previous', label: 'Consolidated Previous', show: this.showConsolidatedColumns && this.hasPreviousYear },
      { type: 'Separate', period: 'Current', label: 'Separate Current', show: true },
      { type: 'Separate', period: 'Previous', label: 'Separate Previous', show: this.hasPreviousYear }
    ];

    this.schema.controls.forEach(control => {
      // Skip section headers and non-required fields
      if (this.isSectionHeader(control) || control.required !== true) return;

      let existingErrors = this.validationErrors.get(control.qname) || [];
      let hasAnyMandatoryError = false;

      columns.forEach(({ type, period, label, show }) => {
        const controlName = this.getXbrlControlName(control.qname, type, period);
        const formControl = this.statementOfCashFlowsIndirectForm.get(controlName);
        const mandatoryError = `${label}: The field is mandatory.`;

        // Skip hidden columns - don't add or keep mandatory errors for columns that aren't displayed
        if (!show) {
          // Remove any existing mandatory error for this hidden column
          existingErrors = existingErrors.filter(err => err !== mandatoryError);
          return;
        }

        if (formControl) {
          const value = formControl.value;
          const isNullOrEmpty = value === null || value === undefined || value === '';

          if (isNullOrEmpty) {
            // Add column-specific error message if not already present
            if (!existingErrors.includes(mandatoryError)) {
              existingErrors.push(mandatoryError);
            }
            hasAnyMandatoryError = true;
          } else {
            // Remove this specific column's mandatory error if value is filled
            existingErrors = existingErrors.filter(err => err !== mandatoryError);
          }
        }
      });

      // Update validation errors
      if (existingErrors.length > 0) {
        this.validationErrors.set(control.qname, existingErrors);
      } else {
        this.validationErrors.delete(control.qname);
      }

      // Update mandatoryFieldErrors for red background
      if (hasAnyMandatoryError) {
        this.mandatoryFieldErrors.add(control.qname);
      } else {
        this.mandatoryFieldErrors.delete(control.qname);
      }
    });
  }

  /**
   * Run all validations (same-sheet + cross-sheet) in full — used at load and
   * in loadSavedState where no edit is in progress. Resets all error stores first
   * so both partitions rebuild from scratch.
   */
  private runAllValidations(): void {
    this.validationErrors.clear();
    this.mandatoryFieldErrors.clear();
    this.crossSheetErrorQnames.clear();
    this.equalityValidationErrors.clear();

    // same-sheet: api-data + consecutive-year + required-fields
    // validateSameSheet preserves crossSheetErrorQnames (empty here), so it
    // clears validationErrors fully and rebuilds the same-sheet partition.
    // cross-sheet: equality rules (r8/r12 recompute) → equalityValidationErrors + validationErrors cross keys
    // Both partitions suppress their own cdr.detectChanges() on this path; one
    // terminal call below replaces the two that would otherwise fire on load/reload.
    this.validateSameSheet(true);
    this.validateCrossSheet(true);
    this.cdr.detectChanges();
  }

  /**
   * Load saved state from service
   * Runs all validations before setting isLoading = false to ensure page shows with validation applied
   */
  private async loadSavedState(): Promise<void> {
    this.stateService.markSheetInitializing(SHEET_KEYS.statementOfCashFlowsIndirectMethod);
    const savedData = this.stateService.getSheetData(SHEET_KEYS.statementOfCashFlowsIndirectMethod);
    if (savedData && Object.keys(savedData).length > 0) {
      this.statementOfCashFlowsIndirectForm.patchValue(savedData, { emitEvent: false });

      // Run calculations and validations before showing the page
      this.calculateTotals();
      this.runAllValidations();

      // All validations complete, show the page
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
      this.isValidationComplete = true;

      // Snapshot baseline after all debounced valueChanges settle
      setTimeout(() => {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log("[On Statement of Cash Flow | Load Saved State] Evaluate Sheet Status START 1.");

        this.evaluateSheetStatus();
        this.stateService.markSheetInitialized(SHEET_KEYS.statementOfCashFlowsIndirectMethod);
      }, 600);
    } else {
      // Still calculate totals so calculated fields show 0.00 instead of blank
      this.calculateTotals();
      this.runAllValidations();

      // All validations complete, show the page
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
      this.isValidationComplete = true;

      // Snapshot baseline after all debounced valueChanges settle
      setTimeout(() => {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log("[On Statement of Cash Flow | Load Saved State] Evaluate Sheet Status START 2.");

        this.evaluateSheetStatus();
        this.stateService.markSheetInitialized(SHEET_KEYS.statementOfCashFlowsIndirectMethod);
      }, 600);
    }
  }

  /**
   * Setup cross-sheet re-validation watches.
   * The auto-save persist sub was collapsed into setupEditPipeline() (single persist).
   */
  private setupStatePersistence(): void {
    // ----------------------------------
    // Cross-sheet local re-validation watch (golden pattern, role-19).
    //
    // Role-18 declared upstream slices (per SheetDependencyService analysis):
    //   - filingPrerequisites (role-0)            — central config hub: currency,
    //     taxonomy variant (MFRS/MPERS), NatureOfFinancialStatements column toggle.
    //   - scopeOfFiling (role-2)                  — consolidation scope.
    //   - statementOfFinancialPosition (role-8)   — Cash and cash equivalents
    //     end-of-period reconciliation (role-520000-mfrs.form.json L2007-2013).
    //   - statementOfProfitOrLoss (role-12)       — ProfitLossBeforeTax
    //     reconciliation (role-520000-mfrs.form.json L38-43).
    //
    // Same-tab SPA navigation back to this sheet should re-validate when any
    // of these upstream slices have changed since last paint.
    this.stateService.modifiedState$
      .pipe(
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.statementOfCashFlowsIndirectMethod)),
        map(state => JSON.stringify({
          r0: state.filingPrerequisites,
          r1: state.filingInfo,
          r2: state.scopeOfFiling,
          r8: state.statementOfFinancialPosition,
          r12: state.statementOfProfitOrLoss,
        })),
        distinctUntilChanged(),
        debounceTime(CROSS_SHEET_VALIDATION_DEBOUNCE_MS),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.validateCrossSheet();

        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log("[On Statement of Cash Flow | Setup State Persistance] Evaluate Sheet Status START 1.");

        this.evaluateSheetStatus();
      });
    // ----------------------------------

    // ----------------------------------
    // Cross-tab safety net — sibling-tab edits to any of the upstream slices
    // listed above must trigger re-validation here too. Idempotent with the
    // modifiedState$ watch (applyRemoteChange also calls
    // modifiedStateSubject.next), but guaranteed to fire even if the hash
    // watch is short-circuited (init guard, schema race, etc.).
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.statementOfCashFlowsIndirectMethod)),
        filter(() => !!this.schema && !!this.statementOfCashFlowsIndirectForm),
        takeUntil(this.destroy$)
      )
      .subscribe(({ sheetKey, changedSheets }) => {
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role18-statementOfCashFlowsIndirectMethod',
          //   sourceSheetKey: sheetKey,
          // });
        }

        // Own-slice change -> repaint this sheet's fields. Other-slice change ->
        // fall through to the cross-sheet revalidation tail only, as before.
        if (changedSheets.includes(SHEET_KEYS.statementOfCashFlowsIndirectMethod))
          this.adoptRemoteState();

        this.validateCrossSheet();

        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log("[On Statement of Cash Flow | Setup State Persistence] Evaluate Sheet Status START 2.");

        this.evaluateSheetStatus();
      });
    // ----------------------------------
  }

  /**
   * Cross-tab adoption for role18 (SoCF indirect). Rows currently open in
   * edit mode are skipped — the user's in-flight work wins until they tick
   * (local overwrites and broadcasts) or press X (the row lands on the
   * adopted value; see `cancelEditMode`).
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoInlineEditSheet`.
   * `calculateTotals()` here recomputes for DISPLAY only; the sibling already
   * persisted its own recomputed totals.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    const openControlNames = [...this.editingRows.values()]
      .flatMap(snapshot => Object.keys(snapshot ?? {}));

    adoptIntoInlineEditSheet(
      this.statementOfCashFlowsIndirectForm,
      this.stateService.getSheetData(SHEET_KEYS.statementOfCashFlowsIndirectMethod),
      openControlNames
    );

    this.calculateTotals();
    this.validateSameSheet();
  }

  /**
   * Check if a row is currently in edit mode
   */
  isRowInEditMode(control: FieldControl): boolean {
    return this.editingRows.has(control.qname);
  }

  /**
   * Enter edit mode for a row - store original values, enable controls
   */
  enterEditMode(control: FieldControl): void {
    // Don't allow editing before validation is complete
    if (!this.isValidationComplete) {
      return;
    }
    // Don't allow editing calculated or readonly fields
    if (this.isCalculatedField(control) || control.ui.readonly) {
      return;
    }

    // Capture the committed badge BEFORE entering edit mode (isRowInEditMode is still false here).
    this.frozenRowStatus.set(control.qname, this.getRowValidationStatus(control));

    // Get all 4 controls for this row
    const controlNames = [
      this.getXbrlControlName(control.qname, 'Consolidated', 'Current'),
      this.getXbrlControlName(control.qname, 'Consolidated', 'Previous'),
      this.getXbrlControlName(control.qname, 'Separate', 'Current'),
      this.getXbrlControlName(control.qname, 'Separate', 'Previous')
    ];

    // Store original values
    const originalValues: { [key: string]: number | null } = {};
    controlNames.forEach(name => {
      originalValues[name] = this.statementOfCashFlowsIndirectForm.get(name)?.value ?? null;
    });
    this.editingRows.set(control.qname, originalValues);
    this.stateService.markSheetEditing(SHEET_KEYS.statementOfCashFlowsIndirectMethod);

    // Enable all 4 controls, but REVEAL only the columns this permutation shows.
    // A hidden column of a required row is permanently null, so `enable()` makes
    // it invalid — revealing it would paint the row red with no text, since every
    // message surface here filters hidden columns out (isVisibleColumnError).
    enableDualColumnRowAndRevealVisible(
      this.statementOfCashFlowsIndirectForm,
      control.qname,
      this.dimensionVisibility,
      { emitEvent: true }
    );
  }

  /**
   * Save edit mode (Tick) — subset persist → same + cross validate → reconcile → clearEditing.
   */
  async saveEditMode(control: FieldControl): Promise<void> {
    if (!this.editingRows.has(control.qname)) {
      return;
    }

    // Get all 4 controls for this row
    const controlNames = [
      this.getXbrlControlName(control.qname, 'Consolidated', 'Current'),
      this.getXbrlControlName(control.qname, 'Consolidated', 'Previous'),
      this.getXbrlControlName(control.qname, 'Separate', 'Current'),
      this.getXbrlControlName(control.qname, 'Separate', 'Previous')
    ];

    // Mark controls as touched for validation display
    controlNames.forEach(name => {
      const formControl = this.statementOfCashFlowsIndirectForm.get(name);
      if (formControl) {
        formControl.markAsTouched();
        formControl.markAsPristine();
        formControl.disable({ emitEvent: false });
      }
    });

    this.frozenRowStatus.delete(control.qname);
    this.editingRows.delete(control.qname);

    // Recalculate totals FIRST so the persisted subset includes the freshly
    // recalculated calculated/derived totals (downstream sheets read these).
    this.calculateTotals();

    // Subset persist — this row's controls PLUS the recalculated calculated totals
    const subset = pickSubset(this.statementOfCashFlowsIndirectForm.getRawValue(), [...controlNames, ...this.calculatedTotalKeys]);
    this.stateService.updateSheetData(SHEET_KEYS.statementOfCashFlowsIndirectMethod, subset, true);

    this.validateSameSheet();
    this.validateCrossSheet();
    this.stateService.reconcileEditedFlag(SHEET_KEYS.statementOfCashFlowsIndirectMethod);
    this.stateService.clearSheetEditing(SHEET_KEYS.statementOfCashFlowsIndirectMethod);
    this.evaluateSheetStatus();
  }

  /**
   * Cancel edit mode - always revert to the values captured when edit mode was entered
   * (discards this session's uncommitted typing). editingRows holds the committed values at entry.
   */
  cancelEditMode(control: FieldControl): void {
    if (!this.editingRows.has(control.qname)) {
      return;
    }

    // Get all 4 controls for this row
    const controlNames = [
      this.getXbrlControlName(control.qname, 'Consolidated', 'Current'),
      this.getXbrlControlName(control.qname, 'Consolidated', 'Previous'),
      this.getXbrlControlName(control.qname, 'Separate', 'Current'),
      this.getXbrlControlName(control.qname, 'Separate', 'Previous')
    ];

    // Revert to the LATEST ADOPTED state rather than the entry-time snapshot, so a
    // sibling tab's confirmed edit that arrived while this row was open is honoured
    // on cancel (P.O. rule 2026-08-17). Falls back to the entry snapshot for any
    // control the state does not carry.
    //
    // In the single-tab case the two are identical — the row is disabled and
    // unwritten for the whole time it is open — so this is a provable no-op there.
    const entryValues = this.editingRows.get(control.qname);
    const adopted = this.stateService.getSheetData(SHEET_KEYS.statementOfCashFlowsIndirectMethod);
    controlNames.forEach((controlName) => {
      const formControl = this.statementOfCashFlowsIndirectForm.get(controlName);
      if (formControl) {
        const revertValue = controlName in adopted
          ? adopted[controlName]
          : (entryValues?.[controlName] ?? null);
        formControl.setValue(revertValue, { emitEvent: false });
        formControl.markAsPristine();
        formControl.disable();
      }
    });

    // Remove from editing set
    this.frozenRowStatus.delete(control.qname);
    this.editingRows.delete(control.qname);

    this.calculateTotals();
    this.validateSameSheet();
    this.stateService.clearSheetEditing(SHEET_KEYS.statementOfCashFlowsIndirectMethod);
    this.evaluateSheetStatus();
  }

  /**
   * Check if an error message references a visible column
   * Used to filter out errors for hidden columns (e.g., Previous year columns when hasPreviousYear is false)
   */
  private isVisibleColumnError(error: string): boolean {
    if (error.includes('Consolidated Current') && !this.showConsolidatedColumns) return false;
    if (error.includes('Consolidated Previous') && (!this.showConsolidatedColumns || !this.hasPreviousYear)) return false;
    if (error.includes('Separate Previous') && !this.hasPreviousYear) return false;
    return true;
  }

  /**
   * Check if a column configuration is visible based on current display settings
   */
  private isColumnVisible(consolidatedOrSeparate: string, period: string): boolean {
    if (consolidatedOrSeparate === 'Consolidated' && !this.showConsolidatedColumns) return false;
    if (period === 'PreviousYear' && !this.hasPreviousYear) return false;
    return true;
  }

  /**
   * Get validation status for a row (checks all 4 columns)
   * Status priority: Failed > Edited > Validated* > Validated
   * Delegates to MappingStatusService.computeRowStatus for consistent Edited detection.
   */
  getRowValidationStatus(control: FieldControl): 'Failed' | 'Edited' | 'Validated*' | 'Validated' {
    // Only check monetary items
    if (control.ui.control !== 'number' && control.ui.controlType !== 'number') return 'Validated';

    if (this.isRowInEditMode(control)) {
      const frozen = this.frozenRowStatus.get(control.qname);
      if (frozen !== undefined) return frozen;
    }

    // Control name format: qname[Consolidated|Separate][CurrentYear|PreviousYear]
    const controlNames = [
      `${control.qname}[Consolidated][CurrentYear]`,
      `${control.qname}[Consolidated][PreviousYear]`,
      `${control.qname}[Separate][CurrentYear]`,
      `${control.qname}[Separate][PreviousYear]`,
    ];

    // Snapshot current values (used by MappingStatusService for Edited detection)
    const currentValues: Record<string, unknown> = {};
    for (const controlName of controlNames) {
      const fc = this.statementOfCashFlowsIndirectForm.get(controlName);
      if (fc) currentValues[controlName] = fc.value;
    }

    const { hasError, hasSuggestedChangeWarning } = this._computeRowErrorFlags(control);

    return this.mappingStatus.computeRowStatus({
      controlNames,
      currentValues,
      lastSubmissionValues: this.mappingStatus.getLastSubmissionValues(
        SHEET_KEYS.statementOfCashFlowsIndirectMethod
      ),
      hasError,
      hasSuggestedChangeWarning,
      forceEdited: false,
      // Calculated totals are computed client-side (calculateTotals) and are absent
      // from the persisted baseline, so the deviation check would mark them Edited
      // with zero user edits. They are never user-editable (enterEditMode skips
      // them) — mark them derived to skip the Edited branch.
      derived: this.isCalculatedField(control),
    });
  }

  /**
   * Compute the row's hasError / hasSuggestedChangeWarning flags. This is the
   * single definition of "does the row have a hard error" vs "only a
   * suggested-change warning" — consumed by both the Mapping Status (which
   * layers the Edited / forceEdited override on top via computeRowStatus) and
   * the row colour intent (which deliberately ignores edit mode). Keeping it in
   * one place means the red/yellow background can never disagree with the
   * Failed/Validated* badge.
   *
   * NOTE: hasError already folds in the cross-sheet condition
   * (equalityValidationErrors / mandatoryFieldErrors) — the SAME predicate as
   * hasCrossSheetValidationError() — so cross-sheet errors resolve to the
   * 'error' (red) intent without a separate guard.
   *
   * Extracted VERBATIM from the former getRowValidationStatus() body so the
   * badge and forceEdited behaviour are unchanged.
   */
  private _computeRowErrorFlags(
    control: FieldControl
  ): { hasError: boolean; hasSuggestedChangeWarning: boolean } {
    // VISIBLE columns only. A column this permutation does not render can never
    // show a message — every message surface filters hidden columns out — so
    // letting one drive the row's colour is what produced a red row with an empty
    // Error Description and a "Validated" badge (UAT, 2026-08-26).
    const controlNames = (
      [
        ['Consolidated', 'CurrentYear'],
        ['Consolidated', 'PreviousYear'],
        ['Separate', 'CurrentYear'],
        ['Separate', 'PreviousYear'],
      ] as ReadonlyArray<[string, string]>
    )
      .filter(([consolidatedOrSeparate, period]) => this.isColumnVisible(consolidatedOrSeparate, period))
      .map(([consolidatedOrSeparate, period]) => `${control.qname}[${consolidatedOrSeparate}][${period}]`);

    // Gather Failed inputs + presence of any value
    let hasInvalid = false;
    let hasValue = false;
    for (const controlName of controlNames) {
      const fc = this.statementOfCashFlowsIndirectForm.get(controlName);
      if (fc) {
        if (fc.invalid && (fc.dirty || fc.touched)) hasInvalid = true;
        if (fc.value !== null && fc.value !== undefined && fc.value !== '') hasValue = true;
      }
    }

    const isRequired = control.required === true;
    let hasError =
      hasInvalid ||
      (isRequired && !hasValue) ||
      this.equalityValidationErrors.has(control.qname) ||
      this.mandatoryFieldErrors.has(control.qname);
    if (!hasError && this.validationErrors.has(control.qname)) {
      const errors = this.validationErrors.get(control.qname) || [];
      hasError = errors.some((message) => !message.includes('does not match the extracted sum'));
    }

    // Suggested-Change warning (incl. linkTo suppression if applicable)
    let hasSuggestedChangeWarning = false;
    if (this.validationErrors.has(control.qname)) {
      const allErrors = this.validationErrors.get(control.qname) || [];
      const allAreSuggestedChanges = allErrors.every((message) =>
        message.includes('does not match the extracted sum')
      );
      hasSuggestedChangeWarning = allAreSuggestedChanges && !control.ui.linkTo;
    }

    return { hasError, hasSuggestedChangeWarning };
  }

  /**
   * Compute the row background colour intent: 'error' (red) when the row has a
   * hard error, 'warning' (yellow) when it only has a suggested-change warning,
   * 'none' otherwise. Red outranks yellow. Unlike the Mapping Status this does
   * NOT consult edit mode, so clicking the edit pencil never repaints the row —
   * the background reflects content independently of edit state.
   */
  private _computeRowColorIntent(control: FieldControl): 'error' | 'warning' | 'none' {
    if (control.ui.control !== 'number' && control.ui.controlType !== 'number') return 'none';
    const { hasError, hasSuggestedChangeWarning } = this._computeRowErrorFlags(control);
    if (hasError) return 'error';
    if (hasSuggestedChangeWarning) return 'warning';
    return 'none';
  }

  /**
   * Public accessor for the row background colour intent (no cache — Role 18
   * does not cache row status, so the template calls the pure computation
   * directly, mirroring how getRowValidationStatus is called).
   * 'error' → red (`row-has-error`), 'warning' → yellow
   * (`row-has-validation-error`), 'none' → no highlight.
   */
  getRowColorIntent(control: FieldControl): 'error' | 'warning' | 'none' {
    return this._computeRowColorIntent(control);
  }

  /**
   * Get error description for a row (aggregates from all 4 columns)
   * Groups errors by category: "Error" (red) and "* Suggested Change" (black)
   */
  getRowErrorDescription(control: FieldControl): string {
    // Only check monetary items
    if (control.ui.control !== 'number' && control.ui.controlType !== 'number') return '';

    const errors: string[] = [];           // Red - Error category (cross-sheet, form control)
    const suggestedChanges: string[] = []; // Black - * Suggested Change category (API mismatch)

    // 1. Process validation errors from validationErrors Map
    if (this.validationErrors.has(control.qname)) {
      const allValidationErrors = this.validationErrors.get(control.qname) || [];

      allValidationErrors.forEach((errorMsg) => {
        if (!this.isVisibleColumnError(errorMsg)) return;

        if (errorMsg.includes('does not match the extracted sum')) {
          suggestedChanges.push(errorMsg);
        } else {
          errors.push(errorMsg);
        }
      });
    }

    // 2. Collect form control validation errors -> Error category
    // Control name format must match getXbrlControlName: qname[Consolidated|Separate][CurrentYear|PreviousYear]
    const columns: Array<{ consolidatedOrSeparate: string; period: string; label: string; show: boolean }> = [
      { consolidatedOrSeparate: 'Consolidated', period: 'CurrentYear', label: 'Consolidated Current', show: this.showConsolidatedColumns },
      { consolidatedOrSeparate: 'Consolidated', period: 'PreviousYear', label: 'Consolidated Previous', show: this.showConsolidatedColumns && this.hasPreviousYear },
      { consolidatedOrSeparate: 'Separate', period: 'CurrentYear', label: 'Separate Current', show: true },
      { consolidatedOrSeparate: 'Separate', period: 'PreviousYear', label: 'Separate Previous', show: this.hasPreviousYear },
    ];

    for (const { consolidatedOrSeparate, period, label, show } of columns) {
      if (!show) continue;

      const controlName = `${control.qname}[${consolidatedOrSeparate}][${period}]`;
      const formControl = this.statementOfCashFlowsIndirectForm.get(controlName);

      if (formControl && formControl.invalid && (formControl.dirty || formControl.touched)) {
        const errorMessages: string[] = [];

        if (formControl.errors) {
          // Note: 'required' error is handled by validateRequiredFields() to avoid duplicates
          if (formControl.errors['min']) errorMessages.push(`Min: ${formControl.errors['min'].min}`);
          if (formControl.errors['max']) errorMessages.push(`Max: ${formControl.errors['max'].max}`);
        }

        if (errorMessages.length > 0) {
          errors.push(`${label}: ${errorMessages.join(', ')}`);
        }
      }
    }

    // 3. Format output grouped by category with CSS classes
    // Error section in red (.error-text), Suggested Change section in black (.suggested-change-text)
    let result = '';

    if (errors.length > 0) {
      result += '<span class="error-text">ERROR</span><br>';
      result += '<span class="error-text">' + errors.map((errorText, errorIndex) => `${errorIndex + 1}. ${errorText}`).join('<br><br>') + '</span>';
    }

    if (suggestedChanges.length > 0) {
      if (result) result += '<br><br>';
      result += '<span class="error-text">*</span><span class="suggested-change-text"> SUGGESTED CHANGE</span><br>';
      result += '<span class="suggested-change-text">' + suggestedChanges.map((changeText, changeIndex) => `${changeIndex + 1}. ${changeText}`).join('<br><br>') + '</span>';
    }

    // Ctrl+Alt+B baseline debug overlay (non-production). Appended to whatever
    // real validation text this row already has (often none for a clean total).
    // baselineDebugForControl returns '' unless this row is a qualifying total.
    // Columns + getControlName rebuilt inline to mirror computeMismatchWarnings'
    // call exactly (all 4 columns + concat keys).
    if (this.baselineDebug.enabled()) {
      const columns = [
        { key: '[Consolidated][CurrentYear]', label: 'Consolidated Current' },
        { key: '[Consolidated][PreviousYear]', label: 'Consolidated Previous' },
        { key: '[Separate][CurrentYear]', label: 'Separate Current' },
        { key: '[Separate][PreviousYear]', label: 'Separate Previous' },
      ];
      const baselineInfo = this.extractedSumWarning.baselineDebugForControl(
        SHEET_KEYS.statementOfCashFlowsIndirectMethod,
        control as unknown as SummableControl,
        columns,
        (qname, key) => qname + key
      );
      if (baselineInfo) {
        result = result ? `${result}${baselineInfo}` : baselineInfo;
      }
    }

    return result;
  }

  /**
   * Check if row has Error category items (for red text styling)
   * Error category includes: cross-sheet validation, form control errors
   */
  hasErrorCategory(control: FieldControl): boolean {
    if (this.equalityValidationErrors.has(control.qname)) return true;
    if (this.mandatoryFieldErrors.has(control.qname)) return true;

    const columns: Array<{ suffix: string; show: boolean }> = [
      { suffix: 'ConsolidatedCurrent', show: this.showConsolidatedColumns },
      { suffix: 'ConsolidatedPrevious', show: this.showConsolidatedColumns && this.hasPreviousYear },
      { suffix: 'SeparateCurrent', show: true },
      { suffix: 'SeparatePrevious', show: this.hasPreviousYear },
    ];

    for (const { suffix, show } of columns) {
      if (!show) continue;

      const controlName = control.qname + suffix;
      const formControl = this.statementOfCashFlowsIndirectForm.get(controlName);

      if (formControl && formControl.invalid && (formControl.dirty || formControl.touched)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if row has Suggested Change category items (for black text styling)
   * Suggested Change category includes: API mismatch warnings
   */
  hasSuggestedChangeCategory(control: FieldControl): boolean {
    if (!this.validationErrors.has(control.qname)) return false;

    const allErrors = this.validationErrors.get(control.qname) || [];

    return allErrors.some((errorMsg) => {
      if (errorMsg.includes('Consolidated Current') && !this.showConsolidatedColumns) return false;
      if (errorMsg.includes('Consolidated Previous') && (!this.showConsolidatedColumns || !this.hasPreviousYear)) return false;
      if (errorMsg.includes('Separate Previous') && !this.hasPreviousYear) return false;

      return errorMsg.includes('does not match the extracted sum');
    });
  }

  /**
   * Check if row has failed validation
   */
  hasRowFailed(control: FieldControl): boolean {
    return this.getRowValidationStatus(control) === 'Failed';
  }

  /**
   * Evaluate sheet status by looping through all rows' mapping status.
   * Sets hasErrors = true if any row has 'Failed' status, false otherwise.
   * Called at init and before submit.
   */
  evaluateSheetStatus(): void {
    if (!this.schema?.controls) return;

    let hasErrors = false;
    for (const control of this.schema.controls) {
      if (this.getRowValidationStatus(control) === 'Failed') {
        hasErrors = true;

        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log("[On Statement of Cash Flow | Evaluate Sheet Status] This Schema Control Failed: ", control);

        break;
      }
    }
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log("[On Statement of Cash Flow | Evaluate Sheet Status] Schema Controls: ", this.schema.controls);
    // console.log("[On Statement of Cash Flow | Evaluate Sheet Status] Has Errors: ", hasErrors);

    this.stateService.updateSheetLevelStatus(SHEET_KEYS.statementOfCashFlowsIndirectMethod, { hasErrors });
  }

  /**
   * Check if row has validation error (for yellow highlighting)
   * Checks validationErrors Map directly instead of status
   * Excludes cross-field validation errors (those should result in 'Failed' status with red background)
   */
  hasRowValidationError(control: FieldControl): boolean {
    if (!this.validationErrors.has(control.qname)) return false;

    const errors = this.validationErrors.get(control.qname) || [];

    // Don't treat cross-field errors as validation errors (they should be 'Failed' not 'Validated*')
    const hasCrossFieldError = errors.some(error =>
      error.includes('Mismatch in value provided for the same field')
    );

    // Only return true if there are errors AND they are NOT cross-field errors
    return !hasCrossFieldError;
  }

  /**
   * Check if a specific field (control + suffix) has any validation error
   * Used to apply red border to individual input fields
   * Checks for: mismatch errors, mandatory errors, cross-sheet errors, etc.
   */
  hasFieldMismatchError(control: FieldControl, suffix: string): boolean {
    if (!this.validationErrors.has(control.qname)) return false;

    const errors = this.validationErrors.get(control.qname) || [];

    // Convert suffix to column label format (e.g., 'ConsolidatedCurrent' -> 'Consolidated Current')
    const columnLabel = suffix.replace('Consolidated', 'Consolidated ').replace('Separate', 'Separate ').replace('Current', 'Current').replace('Previous', 'Previous');

    // Attribute the error to a cell ONLY by its leading "<Column>: " prefix —
    // the single column the message belongs to. A plain `includes(columnLabel)`
    // also matched the OPPOSITE column named inside the message body: the
    // consecutive-year cash messages reference the other column as "(<Column>
    // Year)" (e.g. a "Separate Current:" error on Beginning whose body reads
    // "…(Separate Previous Year)…"), which reddened both the Current and
    // Previous cells of every Beginning/End/End_occurrence2 row. The leading
    // prefix is always "<Column>: "; the body references never carry the colon,
    // so anchoring on `"<columnLabel>:"` isolates the owning column.
    // Suggested changes ("does not match the extracted sum") never trigger red borders.
    return errors.some(error =>
      error.startsWith(`${columnLabel}:`) && !error.includes('does not match the extracted sum')
    );
  }

  /**
   * Custom validation for submit-for-validation component
   * Returns false if there are any 'Failed' status rows (prevents submission)
   */
  validateCashFlowsForm(): boolean {
    if (!this.schema) return true;

    // Check if any row has 'Failed' status
    let hasFailedRows = false;

    this.schema.controls.forEach((control) => {
      if (!this.isSectionHeader(control)) {
        const status = this.getRowValidationStatus(control);
        if (status === 'Failed') {
          hasFailedRows = true;
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // console.log('[CrossSheetValidation] Row has Failed status: ' + control.label + ' (' + control.qname + ')');
        }
      }
    });

    if (hasFailedRows) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('[CrossSheetValidation] Form validation failed - one or more rows have Failed status');
      return false;
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('[CrossSheetValidation] Form validation passed - no Failed status rows');
    return true;
  }

  /**
   * Check if row has equality or mandatory field error (for red highlighting)
   */
  hasCrossSheetValidationError(control: FieldControl): boolean {
    return this.equalityValidationErrors.has(control.qname) || this.mandatoryFieldErrors.has(control.qname);
  }

  onCancelConfirmed(): void {
    // Set flag to skip validation during cancel flow
    this.isCancellingChanges = true;
    this.isValidationComplete = false;

    // Clear all editing states
    this.editingRows.clear();
    this.frozenRowStatus.clear();
    this.stateService.clearSheetEditing(SHEET_KEYS.statementOfCashFlowsIndirectMethod);

    // Clear validation errors
    this.validationErrors.clear();
    this.mandatoryFieldErrors.clear();

    // Reload from the reverted state service data (original API values)
    this.loadSavedState();
    this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
    this.statementOfCashFlowsIndirectForm.markAsPristine();

    // Reset flag after loadSavedState completes (allow time for async operations)
    setTimeout(() => {
      this.isCancellingChanges = false;
    }, 150);
  }

  /**
   * Memoised per-control numeric policy — this sheet's single declaration
   * point for decimals, grouping and the integer-digit cap, all resolved from
   * the control's own schema record rather than hand-written per binding.
   *
   * Memoised because templates call this once per binding inside an `*ngFor`;
   * an unmemoised `resolveNumericPolicy` would re-resolve on every
   * change-detection cycle. Keyed by `qname`, unique per control in a sheet.
   *
   * `[min]`/`[max]` are deliberately NOT bound from here — `SchemaNumericDirective`
   * assigns them onto the PrimeNG instance directly. See that directive's class
   * docblock for why a template binding coerces an unconstrained `undefined`
   * to `NaN` and silently blocks the minus key on signed monetary fields.
   */
  private policyCache = new Map<string, NumericFieldPolicy>();

  numericPolicy(control: FieldControl): NumericFieldPolicy {
    const cached = this.policyCache.get(control.qname);
    if (cached)
      return cached;
    const resolved = resolveNumericPolicy(control);
    this.policyCache.set(control.qname, resolved);
    return resolved;
  }

}
